import dayjs from 'dayjs';

import { countries } from '@/constants';
import { CHANNELS } from '@/models/dashboards/channels.enum';
import { getCountryFlag } from '@/utils/countries';
import { getDateRangeFromDates, getDateRangeOffsetFromToday } from '@/utils/dateUtils';

export const FILTER_OPTIONS_LIMIT = 4;

export const MAX_COMPETITORS = 250;

export const SEARCH_DEBOUNCE_TIME = 250;

export const PAGE_SIZE = 60;

// TODO: temporary fix for [bug ticket] - remove once root cause is fixed and restore to PAGE_SIZE
export const IG_DISCOVERY_PAGE_SIZE = 30;

export const TIKTOK_DISCOVERY_PAGE_SIZE = 50;

export const CREATOR_POST_LIMIT = 16;

export const TIKTOK_DISCOVERY_POST_LIMIT = 8;

export const TIKTOK_FEED_POST_TYPES = [
  'TT_ACCOUNT_FEED',
  'TT_CREATOR_FEED',
  'TT_CREATOR_FEED_CONNECTED',
];

export const TIKTOK_DISCOVERY_SUGGESTED_PROMPTS = [
  {
    label: 'High engagement creators',
    prompt: 'Find creators with high engagement rate',
  },
  {
    label: 'Beauty creators with 50k+ followers',
    prompt: 'Beauty creators with at least 50,000 followers',
  },
  {
    label: 'US-based fitness creators',
    prompt: 'Show me fitness creators located in the United States',
  },
  {
    label: 'Creators with 50k+ average views',
    prompt: 'Creators averaging over 50,000 views per video',
  },
  {
    label: 'Spanish speaking creators',
    prompt: 'Find Spanish-speaking creators',
  },
  {
    label: 'Travel creators with US audience',
    prompt: 'Find travel creators whose audience is in the United States',
  },
  {
    label: 'Creators with Gen Z audience',
    prompt: 'Creators with a Gen Z audience',
  },
  {
    label: 'Low budget creators',
    prompt: 'Find creators with rates under $500',
  },
  {
    label: 'Viral pet creators',
    prompt: 'Pet creators with high median views',
  },
  {
    label: 'Organic beauty creators',
    prompt: 'Organic beauty creators (not paid ads)',
  },
  {
    label: 'Tech creators with mostly male audience',
    prompt: 'Tech creators with mostly male audiences',
  },
  {
    label: 'Fashion creators in Canada',
    prompt: 'Fashion creators located in Canada',
  },
];

export const INSTAGRAM_DISCOVERY_SUGGESTED_PROMPTS = [
  {
    label: 'Female talent with high reel interaction',
    prompt: 'Find me female talent with high reel engagement rate',
  },
  {
    label: 'US creators aged 25-34',
    prompt: 'Creators located in the United States who are aged 25-34',
  },
  {
    label: 'Beauty influencers with 50k-100k followers',
    prompt: 'Influencers with 50k to 100k follower range focused on beauty content',
  },
  {
    label: 'High engagement creators',
    prompt: 'Find creators with high reel interaction',
  },
  {
    label: 'Beauty creators with 50k+ followers',
    prompt: 'Beauty creators with at least 50,000 followers',
  },
  {
    label: 'US-based fitness creators',
    prompt: 'Show me fitness creators located in the United States',
  },
  {
    label: 'Creators aged 25–34',
    prompt: 'Creators who are between 25 and 34 years old',
  },
  {
    label: 'Travel creators with US audience',
    prompt: 'Find travel creators whose audience is in the United States',
  },
  {
    label: 'Creators who mentioned us',
    prompt: 'Which creators have mentioned our brand?',
  },
  {
    label: 'Food creators in Toronto',
    prompt: 'Any food creators in Toronto?',
  },
  {
    label: 'Creators with engaged audiences',
    prompt: 'Creators with at least 10,000 engaged accounts',
  },
  {
    label: 'Creators with female audiences',
    prompt: 'Find creators whose audience is mostly female',
  },
];

export const CONTENT_DISCOVERY_FILTER_DEFAULTS = {
  sourceCreated: { preset: 'ALL_TIME' },
  sentiment: [],
  locations: [],
  competitorHandles: [],
  visualFeatures: undefined,
  followersRange: { min: null, max: null },
  industryIds: [],
  keywordsAndHashtags: undefined,
};

export const CONTENT_DISCOVERY_SUGGESTED_PROMPTS = {
  TIKTOK: [
    {
      label: 'Top skincare posts',
      prompt: 'Find #skincare posts sorted by highest engagements from the last 30 days',
    },
    {
      label: 'GRWM content this week',
      prompt: 'Show me posts about "get ready with me" from the last 7 days',
    },
    {
      label: 'Positive wellness posts',
      prompt: 'Show me posts about "wellness" with positive sentiment from the last 30 days',
    },
    {
      label: 'Top beauty posts',
      prompt: 'Find posts using #beautytok sorted by highest engagements from the last month',
    },
    {
      label: 'Positive lifestyle content',
      prompt: 'Show me #lifestyle posts with positive sentiment from the last 30 days',
    },
    {
      label: 'Top fashion posts',
      prompt: 'Show me #ootd posts sorted by highest engagements from the last 30 days',
    },
    {
      label: 'Trending hair posts',
      prompt: 'Find #hairtok posts sorted by highest engagements from the last 30 days',
    },
    {
      label: 'Positive travel content',
      prompt: 'Show me posts about "travel" with positive sentiment from the last month',
    },
    {
      label: 'Food posts by location',
      prompt: 'Find posts using #foodtok based in New York from the last 30 days',
    },
    {
      label: 'Top fitness content',
      prompt: 'Show me posts about "gym" sorted by highest engagements from the last 7 days',
    },
  ],
  INSTAGRAM: [
    {
      label: 'Skincare posts under 100K',
      prompt: 'Find posts about #skincare from accounts with under 100K followers',
    },
    {
      label: 'Positive beauty posts',
      prompt: 'Show me #beauty posts with positive sentiment from the last 30 days',
    },
    {
      label: 'Competitor tagged posts',
      prompt: 'Find posts that have tagged [@competitor] in the last 30 days',
    },
    {
      label: 'Morning routine content',
      prompt:
        'Show me posts about "morning routine" from accounts with between 10K and 250K followers',
    },
    {
      label: 'Street style in New York',
      prompt: 'Find posts using #streetstyle based in New York in the last month',
    },
    {
      label: 'Trending food posts',
      prompt: 'Show me #foodie posts with positive sentiment from the last 30 days',
    },
    {
      label: 'Recent #ad posts',
      prompt: 'Find posts using #ad from the last week',
    },
    {
      label: 'Fitness content under 100K',
      prompt: 'Show me posts about "workout" from accounts with under 100K followers',
    },
    {
      label: 'Fashion posts in New York',
      prompt: 'Find posts using #fashion based in New York from the last 30 days',
    },
    {
      label: 'Summer outfit content',
      prompt: 'Show me posts about "summer outfits" from the last month',
    },
    {
      label: 'Mid-tier GRWM posts',
      prompt: 'Find #grwm posts from accounts with between 50K and 500K followers',
    },
    {
      label: 'Multi-competitor posts',
      prompt: 'Show me posts that have tagged [@competitor] or [@competitor] in the last 30 days',
    },
  ],
  YOUTUBE: [
    {
      label: 'Top tech review posts',
      prompt:
        'Find posts about "tech AND review" sorted by highest engagements from the last 30 days',
    },
    {
      label: 'Positive beauty tutorials',
      prompt:
        'Show me posts about "makeup AND tutorial" with positive sentiment from the last 30 days',
    },
    {
      label: 'Competitor tagged posts',
      prompt: 'Find posts that have tagged [@competitor] in the last month',
    },
    {
      label: 'Recent unboxing content',
      prompt: 'Show me posts about "unboxing" from the last 7 days',
    },
    {
      label: 'GRWM content',
      prompt: 'Find posts about "grwm" from the last 7 days',
    },
    {
      label: 'Skincare & makeup content',
      prompt: 'Show me posts about "skincare OR makeup" from the last month',
    },
    {
      label: 'Newly launched products',
      prompt: 'Find posts about "new AND launch" from the last week',
    },
    {
      label: 'Competitor product reviews',
      prompt: 'Show me posts about "product AND review" that have tagged [@competitor]',
    },
    {
      label: 'Home recipes content this week',
      prompt: 'Find posts about "recipes AND home" from the last 7 days',
    },
    {
      label: 'Travel vlog content',
      prompt: 'Show me posts about "travel" with positive sentiment from the last month',
    },
    {
      label: 'Top fitness content',
      prompt: 'Find posts about "workout" sorted by highest engagements from the last 7 days',
    },
    {
      label: 'Haul content this month',
      prompt: 'Show me posts about "haul" from the last 30 days',
    },
  ],
};

export const DISCOVERY_NLS_PLACEHOLDERS = {
  INSTAGRAM: {
    CREATOR:
      "Search for an exact handle or describe what you're looking for like audience demographics, location, reel engagement rate, etc...",
    CONTENT:
      "Search by keyword, #hashtag, @mention and/or describe what you're looking for like publish date, location, industry, etc...",
  },
  TIKTOK: {
    CREATOR:
      "Search for exact handles or describe what you're looking for like audience demographics, location, interests, etc...",
    CONTENT:
      "Search by keyword, #hashtag, @mention and/or describe what you're looking for like publish date, location, industry, etc...",
  },
  YOUTUBE: {
    CONTENT:
      "Search by keyword, #hashtag, @mention and/or describe what you're looking for like publish date, location, industry, etc...",
  },
};

export const CREATOR_CAMPAIGNS_PAGE_SIZE = 9;

export const CREATOR_IN_CAMPAIGN_PAGE_SIZE = 1;

export const TROLLEY_SUBDOMAIN_REGEX = /^(?!-)(?=.*[a-z-])[a-z0-9-]{4,63}(?<!-)$/;

export const PLATFORM_PRIORITY = ['instagram', 'tiktok', 'youtube'];

export const CREATOR_PROFILE_SUPPORTED_CHANNELS = new Set([
  CHANNELS.INSTAGRAM.value,
  CHANNELS.TIKTOK.value,
  CHANNELS.YOUTUBE.value,
]);

export const CREATOR_DRAWERS = {
  CREATOR_PROFILE: 'creatorProfile',
  ACCOUNT_PROFILE: 'accountProfile',
  SIMILAR_CREATORS: 'similarCreators',
  BULK_UPLOAD: 'creatorBulkUpload',
  INFLUENTIAL_FANS: 'influentialFans',
  FIT_INSIGHTS: 'creatorFitInsights',
  FIT_INSIGHTS_BRAND_FIT_POSTS: 'creatorFitInsightsBrandFitPosts',
  FIT_INSIGHTS_UGC_POSTS: 'creatorFitInsightsUgcPosts',
  OCAPI_CONNECT: 'ocapiConnect',
};

export const tooltips = {
  importedCreatorEmail:
    "This email was automatically imported from the Creator's bio. You can edit it at any time.",
  tiktokAudienceRegions: 'The region that the majority of followers are based in',
  tiktokAudienceAge: "The age range that the majority of the creators' followers fall into",
  tiktokAudienceGender: 'The gender that the majority of the creators’ followers fall into',
  tiktokMedianViews: "The middle value in the range of views across the creator's recent 30 videos",
  tiktokSparkAdsHeader:
    'Requesting permission from the creator will allow your brand to promote this content as a TikTok Spark Ad. This is available only if the creator has associated the post with your Campaign via invite link.',
  tiktokSparkAdsAuthCode:
    'Copy and paste this code when setting up a Spark Ad, or click Sync to Ad Account to send it directly to your authorized posts list in TikTok Ads Manager',
  tiktokCompetitiveMentionsTooltip:
    'Configuring TikTok competitors applies only to creator competitive mentions and not the competitors feature suite',
  customFields:
    'Collect creator details unique to your brand. Custom fields can be added or managed in Settings.',
  metaPaidPartnerAds:
    'Requesting access from the creator grants account-level permission to run paid partnership ads on their posts that feature your brand. Track the status of your request here.',
  cityFilter:
    'The estimated city and/or country where a creator resides, based on publicly available profile data. Creators with only a country estimate will appear when searching by country.',
  contentDiscoveryCompetitors: 'Filter by content mentioning your competitors',
  contentDiscoveryCityFilter:
    'The estimated city and/or country, based on publicly available data. Only content with a country estimate will appear when filter is applied.',
  contentDiscoveryReportingPeriod: 'Filter by content published within the reporting period',
  disabledDiscoveryTab: 'Add to Your Creators so you can access this feature',
  disabledTikTokChannelOption: 'Please connect your TikTok One Marketplace Account',
  disabledFilterDueToRateLimit:
    "This filter isn't available when rate limit is reached. Please come back later to use this filter.",
  disabledFilterDueToConflict:
    'This filter can’t be used with your current selection. Clear or adjust other filters to enable it.',
};

export const citiesFilterSearchPlaceholder = 'Search City or Country';

export const sparkAdsCodeStatus = {
  IN_USE: 'IN_USE',
  NOT_USED: 'NOT_USED',
  DELETED: 'DELETED',
  EXPIRED: 'EXPIRED',
};

export const SparkAdsLearnMoreUrl = 'https://help.dashsocial.com/hc/en-us/articles/31626086483341';

export const metaPaidPartnerAdsUrl = 'https://help.dashsocial.com/hc/en-us/articles/34060247436301';

const discoveryInterests = [
  {
    label: 'Animals and Pets',
    value: 'Animals and Pets',
    children: [
      { label: 'Animals', value: 'Animals' },
      { label: 'Cats', value: 'Cats' },
      { label: 'Dogs', value: 'Dogs' },
      { label: 'Pets', value: 'Pets' },
    ],
  },
  {
    label: 'Arts and Music',
    value: 'Arts and Music',
    children: [
      { label: 'Acting', value: 'Acting' },
      { label: 'Art', value: 'Art' },
      { label: 'Ceramics', value: 'Ceramics' },
      { label: 'Country Music', value: 'Country Music' },
      { label: 'Dance', value: 'Dance' },
      { label: 'Hip Hop', value: 'Hip Hop' },
      { label: 'Jazz', value: 'Jazz' },
      { label: 'Music', value: 'Music' },
      { label: 'Painting', value: 'Painting' },
      { label: 'Photography', value: 'Photography' },
      { label: 'Singing', value: 'Singing' },
      { label: 'Tattoos', value: 'Tattoos' },
      { label: 'Theater', value: 'Theater' },
      { label: 'Writing', value: 'Writing' },
      { label: 'Concerts', value: 'Concerts' },
    ],
  },
  {
    label: 'Automotive',
    value: 'Automotive',
    children: [
      { label: 'Cars', value: 'Cars' },
      { label: 'Motorcycles', value: 'Motorcycles' },
    ],
  },
  {
    label: 'Beauty and Esthetics',
    value: 'Beauty and Esthetics',
    children: [
      { label: 'Beauty', value: 'Beauty' },
      { label: 'Botox', value: 'Botox' },
      { label: 'Fragrance', value: 'Fragrance' },
      { label: 'Hair', value: 'Hair' },
      { label: 'Hairstyling', value: 'Hairstyling' },
      { label: 'Makeup', value: 'Makeup' },
      { label: 'Nails', value: 'Nails' },
      { label: 'Skincare', value: 'Skincare' },
    ],
  },
  {
    label: 'Business and Entrepreneurship',
    value: 'Business and Entrepreneurship',
    children: [
      { label: 'Business', value: 'Business' },
      { label: 'Entrepreneurship', value: 'Entrepreneurship' },
      { label: 'Leadership', value: 'Leadership' },
      { label: 'Marketing', value: 'Marketing' },
      { label: 'Real Estate', value: 'Real Estate' },
      { label: 'Small Business', value: 'Small Business' },
      { label: 'Graphic Design', value: 'Graphic Design' },
    ],
  },
  {
    label: 'Clothing and Accessories',
    value: 'Clothing and Accessories',
    children: [
      { label: 'Accessories', value: 'Accessories' },
      { label: 'Activewear', value: 'Activewear' },
      { label: 'Fashion', value: 'Fashion' },
      { label: 'Jewelry', value: 'Jewelry' },
      { label: 'Lingerie', value: 'Lingerie' },
      { label: "Men's Fashion", value: "Men's Fashion" },
      { label: 'Shoes', value: 'Shoes' },
      { label: 'Streetwear', value: 'Streetwear' },
      { label: 'Sustainable Fashion', value: 'Sustainable Fashion' },
      { label: 'Swimwear', value: 'Swimwear' },
      { label: 'Watches', value: 'Watches' },
    ],
  },
  {
    label: 'Design and Home',
    value: 'Design and Home',
    children: [
      { label: 'DIY', value: 'DIY' },
      { label: 'Architecture', value: 'Architecture' },
      { label: 'Design', value: 'Design' },
      { label: 'Floral Design', value: 'Floral Design' },
      { label: 'Home Decor', value: 'Home Decor' },
      { label: 'Interior Design', value: 'Interior Design' },
      { label: 'Renovation', value: 'Renovation' },
    ],
  },
  {
    label: 'Media and Entertainment',
    value: 'Media and Entertainment',
    children: [
      { label: 'Books', value: 'Books' },
      { label: 'Comedy', value: 'Comedy' },
      { label: 'Entertainment', value: 'Entertainment' },
      { label: 'Events', value: 'Events' },
      { label: 'Festivals', value: 'Festivals' },
      { label: 'Gaming', value: 'Gaming' },
      { label: 'Movies', value: 'Movies' },
      { label: 'Podcasting', value: 'Podcasting' },
      { label: 'Reality TV', value: 'Reality TV' },
      { label: 'Toys', value: 'Toys' },
      { label: 'TV Shows', value: 'TV Shows' },
    ],
  },
  {
    label: 'Family and Relationships',
    value: 'Family and Relationships',
    children: [
      { label: 'Family', value: 'Family' },
      { label: 'Friendship', value: 'Friendship' },
      { label: 'LGBTQ+', value: 'LGBTQ+' },
      { label: 'Marriage', value: 'Marriage' },
      { label: 'Motherhood', value: 'Motherhood' },
      { label: 'Parenting', value: 'Parenting' },
      { label: 'Pregnancy', value: 'Pregnancy' },
      { label: 'Relationships', value: 'Relationships' },
      { label: 'Weddings', value: 'Weddings' },
    ],
  },

  {
    label: 'Fitness and Training',
    value: 'Fitness and Training',
    children: [
      { label: 'Barre', value: 'Barre' },
      { label: 'Bodybuilding', value: 'Bodybuilding' },
      { label: 'Crossfit', value: 'Crossfit' },
      { label: 'Fitness', value: 'Fitness' },
      { label: 'Gym', value: 'Gym' },
      { label: 'Personal Training', value: 'Personal Training' },
      { label: 'Pilates', value: 'Pilates' },
      { label: 'Running', value: 'Running' },
      { label: 'Strength Training', value: 'Strength Training' },
      { label: 'Yoga', value: 'Yoga' },
    ],
  },
  {
    label: 'Food and Beverage',
    value: 'Food and Beverage',
    children: [
      { label: 'Baking', value: 'Baking' },
      { label: 'Cocktails', value: 'Cocktails' },
      { label: 'Coffee', value: 'Coffee' },
      { label: 'Cooking', value: 'Cooking' },
      { label: 'Desserts', value: 'Desserts' },
      { label: 'Food', value: 'Food' },
      { label: 'Gluten-free', value: 'Gluten-free' },
      { label: 'Healthy Eating', value: 'Healthy Eating' },
      { label: 'Recipes', value: 'Recipes' },
      { label: 'Tea', value: 'Tea' },
      { label: 'Vegan', value: 'Vegan' },
      { label: 'Wine', value: 'Wine' },
    ],
  },
  {
    label: 'Health and Wellness',
    value: 'Health and Wellness',
    children: [
      { label: 'Body Positivity', value: 'Body Positivity' },
      { label: 'Gut Health', value: 'Gut Health' },
      { label: 'Health', value: 'Health' },
      { label: 'Holistic Health', value: 'Holistic Health' },
      { label: 'Meditation', value: 'Meditation' },
      { label: 'Nutrition', value: 'Nutrition' },
      { label: 'Self-care', value: 'Self-care' },
      { label: 'Sleep', value: 'Sleep' },
      { label: 'Wellness', value: 'Wellness' },
      { label: 'Well-being', value: 'Well-being' },
    ],
  },
  {
    label: 'Luxury',
    value: 'Luxury',
  },
  {
    label: 'Nature and Outdoor Activities',
    value: 'Nature and Outdoor Activities',
    children: [
      { label: 'Beaches', value: 'Beaches' },
      { label: 'Flowers', value: 'Flowers' },
      { label: 'Mountains', value: 'Mountains' },
      { label: 'Nature', value: 'Nature' },
      { label: 'Plants', value: 'Plants' },
      { label: 'Camping', value: 'Camping' },
      { label: 'Fishing', value: 'Fishing' },
      { label: 'Gardening', value: 'Gardening' },
      { label: 'Hiking', value: 'Hiking' },
      { label: 'Hunting', value: 'Hunting' },
      { label: 'Outdoors', value: 'Outdoors' },
    ],
  },
  {
    label: 'Political and Social',
    value: 'Political and Social',
    children: [
      { label: 'Activism', value: 'Activism' },
      { label: 'Charity', value: 'Charity' },
      { label: 'Culture', value: 'Culture' },
      { label: 'Education', value: 'Education' },
      { label: 'Faith', value: 'Faith' },
      { label: 'History', value: 'History' },
      { label: 'Politics', value: 'Politics' },
      { label: 'Social Justice', value: 'Social Justice' },
      { label: 'Spirituality', value: 'Spirituality' },
      { label: 'Sustainability', value: 'Sustainability' },
    ],
  },
  {
    label: 'Sports and Athletics',
    value: 'Sports and Athletics',
    children: [
      { label: 'Basketball', value: 'Basketball' },
      { label: 'Boxing', value: 'Boxing' },
      { label: 'Cycling', value: 'Cycling' },
      { label: 'Football', value: 'Football' },
      { label: 'Golf', value: 'Golf' },
      { label: 'Skateboarding', value: 'Skateboarding' },
      { label: 'Skiing', value: 'Skiing' },
      { label: 'Snowboarding', value: 'Snowboarding' },
      { label: 'Soccer', value: 'Soccer' },
      { label: 'Sports', value: 'Sports' },
      { label: 'Surfing', value: 'Surfing' },
      { label: 'Tennis', value: 'Tennis' },
      { label: 'Wrestling', value: 'Wrestling' },
    ],
  },
  {
    label: 'Technology',
    value: 'Technology',
  },
  {
    label: 'Travel',
    value: 'Travel',
  },
];

const tikTokDiscoveryEngagementRateOptions = [
  { value: '0-5', label: '0% - 5%', minEngagementRate: 0, maxEngagementRate: 5 },
  { value: '5-10', label: '5% - 10%', minEngagementRate: 5, maxEngagementRate: 10 },
  { value: '10-15', label: '10% - 15%', minEngagementRate: 10, maxEngagementRate: 15 },
  { value: '15-20', label: '15% - 20%', minEngagementRate: 15, maxEngagementRate: 20 },
  { value: '>20', label: '>20%', minEngagementRate: 20, maxEngagementRate: null },
];

const instagramDiscoveryAgeOptions = [
  { value: '18_to_24', label: '18-24', minAge: 18, maxAge: 24 },
  { value: '25_to_34', label: '25-34', minAge: 25, maxAge: 34 },
  { value: '35_to_44', label: '35-44', minAge: 35, maxAge: 44 },
  { value: '45_to_54', label: '45-54', minAge: 45, maxAge: 54 },
  { value: '55_to_64', label: '55-64', minAge: 55, maxAge: 64 },
  { value: '65_and_above', label: '65 and above', minAge: 65, maxAge: null },
];

const instagramDiscoveryGenderOptions = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
];

const instagramDiscoveryReelsInteractionOptions = [
  { value: 'over_3_percent', label: '3% and above' },
  { value: 'over_5_percent', label: '5% and above' },
  { value: 'over_10_percent', label: '10% and above' },
];

const instagramDiscoveryEngagedAccountsOptions = {
  min: [0, 2000, 10000, 50000, 100000],
  max: [2000, 10000, 50000, 100000],
};

export const errors = {
  invalidBirthday: 'Please enter a valid birthday',
  invalidBirthYear: 'Please enter a valid birth year',
  invalidPhoneNumber: 'Please enter a valid phone number',
  invalidEmail: 'Please enter a valid email address',
};

export const creatorsAccessMessages = {
  noUserAccessTitle: "Looks like you don't have permission to use this feature!",
  noUserAccessMessage:
    'Dash Social brings you all of the insights you love, including Creators! Ask your Dash Social administrator to provide you access.',
};

export const routeDetails = {
  CREATORS: {
    name: 'creators',
    path: '',
    title: 'Creators',
  },
  OVERVIEW: {
    name: 'creators.overview',
    path: 'overview',
    title: 'Overview',
  },
  YOUR_CREATORS: {
    name: 'creators.your-creators',
    path: 'your-creators',
    title: 'Your Creators',
  },
  CREATOR_DISCOVERY: {
    name: 'creators.discovery',
    path: 'discovery',
    title: 'Creator Discovery',
  },
  TIKTOK_DISCOVERY: {
    name: 'creators.discovery.tiktok',
    path: 'discovery/tiktok',
    title: 'TikTok',
    channel: 'tiktok',
  },
  INSTAGRAM_DISCOVERY: {
    name: 'creators.discovery.instagram',
    path: 'discovery/instagram',
    title: 'Instagram',
    channel: 'instagram',
  },
  YOUTUBE_DISCOVERY: {
    name: 'creators.discovery.youtube',
    path: 'discovery/youtube',
    title: 'YouTube',
    channel: 'youtube',
  },
  CREATOR_PAYMENT: {
    name: 'creators.payment',
    path: 'payment',
    title: 'Payments',
  },
  PAYEES: {
    name: 'creators.payment.payees',
    path: 'payment/payees',
    title: 'Payees',
  },
  PAYMENT_HISTORY: {
    name: 'creators.payment.payment-history',
    path: 'payment/payment-history',
    title: 'Payment History',
  },
  INFLUENTIAL_FANS_IG: {
    name: 'creators.influential-fans.instagram',
    path: 'influential-fans/instagram',
    title: 'Instagram',
    channel: 'instagram',
  },
  INFLUENTIAL_FANS_TT: {
    name: 'creators.influential-fans.tiktok',
    path: 'influential-fans/tiktok',
    title: 'TikTok',
    channel: 'tiktok',
  },
};

export const metricDetails = {
  postCount: {
    label: 'Posts Feat. You',
    metricValue: 'postCount',
    listMetricValue: 'postCount',
    sortValue: 'TOTAL_POSTS_FEAT_YOU',
    isRIQMetric: true,
  },
  avgEmv: {
    label: 'Avg. EMV',
    formatType: 'currency',
    formatOptions: { maximumFractionDigits: 0 },
    metricValue: 'avgEmv',
    listMetricValue: 'avgEmv',
    sortValue: 'AVG_EMV',
    isRIQMetric: true,
  },
  avgEngagements: {
    label: 'Avg. Engagements',
    formatType: 'abbr',
    metricValue: 'avgEngagements',
    listMetricValue: 'avgEngagements',
    sortValue: 'AVG_ENGAGEMENT',
    isRIQMetric: true,
  },
  avgEngagementRate: {
    label: 'Avg. Engagement Rate',
    formatType: 'percent',
    formatOptions: { style: 'percent', maximumFractionDigits: 2 },
    metricValue: 'avgEngagementRate',
    listMetricValue: 'avgEngagementRate',
    sortValue: 'AVG_ENGAGEMENT_RATE',
    isRIQMetric: true,
  },
  totalEngagements: {
    label: 'Total Engagements',
    formatType: 'abbr',
    metricValue: 'totalEngagements',
    listMetricValue: 'totalEngagements',
    sortValue: 'TOTAL_ENGAGEMENTS',
    isRIQMetric: true,
  },
  totalEmv: {
    label: 'EMV',
    formatType: 'currency',
    formatOptions: { maximumFractionDigits: 0 },
    metricValue: 'totalEmv',
    listMetricValue: 'totalEmv',
    sortValue: 'TOTAL_EMV',
    isRIQMetric: true,
  },
  avgReach: {
    label: 'Avg. Reach',
    disconnectedLabel: 'Avg. Estimated Reach',
    formatType: 'abbr',
    metricValue: 'avgReach',
    listMetricValue: 'avgReach',
    sortValue: 'AVG_REACH',
    isRIQMetric: true,
  },
  avgImpressions: {
    label: 'Avg. Impressions',
    formatType: 'abbr',
    metricValue: 'avgImpressions',
    listMetricValue: 'avgImpressions',
    sortValue: 'AVG_IMPRESSIONS',
    isRIQMetric: true,
  },
  avgEffectiveness: {
    label: 'Avg. Effectiveness',
    formatType: 'percent',
    formatOptions: { style: 'percent', maximumFractionDigits: 2 },
    metricValue: 'avgEffectiveness',
    listMetricValue: 'avgEffectiveness',
    sortValue: 'AVG_EFFECTIVENESS',
    isRIQMetric: true,
  },
  followers: {
    label: 'Followers',
    formatType: 'abbr',
    metricValue: 'followers',
    listMetricValue: 'followers',
    sortValue: 'FOLLOWERS',
  },
  followersGained: {
    label: 'Followers Gained',
    formatType: 'abbr',
    metricValue: 'followersGained',
    listMetricValue: 'followersGained',
    sortValue: 'FOLLOWERS_GAINED',
    isRIQMetric: true,
  },
  instagramFollowers: {
    label: 'Instagram Followers',
    formatType: 'abbr',
    metricValue: 'followers',
    listMetricValue: 'instagramFollowers',
    sortValue: 'INSTAGRAM_FOLLOWERS',
  },
  tiktokFollowers: {
    label: 'TikTok Followers',
    formatType: 'abbr',
    metricValue: 'followers',
    listMetricValue: 'tiktokFollowers',
    sortValue: 'TIKTOK_FOLLOWERS',
  },
  totalImpressions: {
    label: 'Total Impressions',
    formatType: 'abbr',
    metricValue: 'totalImpressions',
    listMetricValue: 'totalImpressions',
    sortValue: 'TOTAL_IMPRESSIONS',
    isRIQMetric: true,
  },
  avgCompletionRate: {
    label: 'Avg. Completion Rate',
    formatType: 'percent',
    formatOptions: { style: 'percent', maximumFractionDigits: 2 },
    metricValue: 'avgCompletionRate',
    listMetricValue: 'avgCompletionRate',
    sortValue: 'AVG_COMPLETION_RATE',
    isRIQMetric: true,
  },
  avgExitRate: {
    label: 'Avg. Exit Rate',
    formatType: 'percent',
    formatOptions: { style: 'percent', maximumFractionDigits: 2 },
    metricValue: 'avgExitRate',
    listMetricValue: 'avgExitRate',
    sortValue: 'AVG_EXIT_RATE',
    isRIQMetric: true,
  },
  avgVideoViews: {
    label: 'Avg. Video Views',
    newLabel: 'Avg. Views',
    formatType: 'abbr',
    metricValue: 'avgVideoViews',
    listMetricValue: 'avgVideoViews',
    sortValue: 'AVG_VIDEO_VIEWS',
    isRIQMetric: true,
  },
  medianViews: {
    label: 'Median Views',
    formatType: 'abbr',
    metricValue: 'medianViews',
    listMetricValue: 'medianViews',
    sortValue: 'MEDIAN_VIEWS',
  },
  views: {
    label: 'Total Video Views',
    newLabel: 'Total Views',
    formatType: 'abbr',
    metricValue: 'views',
    listMetricValue: 'views',
    sortValue: 'VIDEO_VIEWS',
    isRIQMetric: true,
  },
  // Need a time format for these two - add in GDI
  avgTimeWatched: {
    label: 'Avg. Time Watched',
    formatType: 'abbr',
    metricValue: 'avgTimeWatched',
    listMetricValue: 'avgTimeWatched',
    sortValue: 'AVG_TIME_WATCHED',
  },
  totalTimeWatched: {
    label: 'Total Time Watched',
    formatType: 'abbr',
    metricValue: 'totalTimeWatched',
    listMetricValue: 'totalTimeWatched',
    sortValue: 'TOTAL_TIME_WATCHED',
  },
  youtubeSubscribers: {
    label: 'YouTube Subscribers',
    formatType: 'abbr',
    metricValue: 'followers',
    listMetricValue: 'youtubeSubscribers',
    sortValue: 'YOUTUBE_SUBSCRIBERS',
  },
  youtubeVideos: {
    label: 'Posts',
    formatType: 'abbr',
    metricValue: 'postCount',
    listMetricValue: 'youtubeVideos',
    sortValue: 'POST_COUNT',
  },
  youtubeViews: {
    label: 'Views',
    formatType: 'abbr',
    metricValue: 'totalViews',
    listMetricValue: 'youtubeViews',
    sortValue: 'VIDEO_VIEWS',
  },
  youtubeSubscribersOnly: {
    label: 'Subscribers',
    formatType: 'abbr',
    metricValue: 'followers',
    listMetricValue: 'youtubeSubscribers',
    sortValue: 'YOUTUBE_SUBSCRIBERS',
  },
};

export const creatorCardMetrics = {
  INSTAGRAM: [
    metricDetails.postCount,
    metricDetails.avgEngagementRate,
    metricDetails.avgEngagements,
    metricDetails.avgEmv,
  ],
  INSTAGRAM_STORIES_ONLY: [
    metricDetails.postCount,
    metricDetails.avgImpressions,
    metricDetails.avgCompletionRate,
    metricDetails.avgEmv,
  ],
  TIKTOK: [
    metricDetails.postCount,
    metricDetails.avgEngagementRate,
    metricDetails.avgEngagements,
    metricDetails.avgEmv,
  ],
  YOUTUBE: [
    metricDetails.postCount,
    metricDetails.youtubeSubscribersOnly,
    metricDetails.avgEngagementRate,
    metricDetails.avgEngagements,
    metricDetails.avgEmv,
  ],
  MULTIPLE_CHANNELS: [],
};

// YouTube compliance: avg engagement rate / engagements / EMV are not approved for YouTube;
// hide them from Your Creators cards, list, and sort while the flag is off.
export const YOUTUBE_COMPLIANCE_RESTRICTED_SORT_VALUES = [
  'AVG_ENGAGEMENT_RATE',
  'AVG_ENGAGEMENT',
  'AVG_EMV',
];

// Single-channel followers/subscribers metric, shown right after Posts Feat. You in grid + list.
export const FOLLOWERS_METRIC_BY_PLATFORM = {
  INSTAGRAM: metricDetails.instagramFollowers,
  TIKTOK: metricDetails.tiktokFollowers,
  YOUTUBE: metricDetails.youtubeSubscribersOnly,
};

export const defaultTikTokRegions = [
  { label: 'United States', code: 'US', value: 'US' },
  { label: 'Canada', code: 'CA', value: 'CA' },
  { label: 'United Kingdom', code: 'GB', value: 'GB' },
  { label: 'Australia', code: 'AU', value: 'AU' },
];

export const defaultTikTokOtherCountries = [
  { value: 'AU', label: 'Australia', code: 'AU' },
  { value: 'CA', label: 'Canada', code: 'CA' },
  { value: 'DE', label: 'Germany', code: 'DE' },
  { value: 'GB', label: 'United Kingdom', code: 'GB' },
];

const tiktokRegions = [
  { label: 'Argentina', code: 'AR', value: 'AR' },
  { label: 'Australia', code: 'AU', value: 'AU' },
  { label: 'Brazil', code: 'BR', value: 'BR' },
  { label: 'Canada', code: 'CA', value: 'CA' },
  { label: 'Colombia', code: 'CO', value: 'CO' },
  { label: 'Egypt', code: 'EG', value: 'EG' },
  { label: 'France', code: 'FR', value: 'FR' },
  { label: 'Germany', code: 'DE', value: 'DE' },
  { label: 'Indonesia', code: 'ID', value: 'ID' },
  { label: 'Israel', code: 'IL', value: 'IL' },
  { label: 'Italy', code: 'IT', value: 'IT' },
  { label: 'Japan', code: 'JP', value: 'JP' },
  { label: 'Malaysia', code: 'MY', value: 'MY' },
  { label: 'Mexico', code: 'MX', value: 'MX' },
  { label: 'Philippines', code: 'PH', value: 'PH' },
  { label: 'Saudi Arabia', code: 'SA', value: 'SA' },
  { label: 'Singapore', code: 'SG', value: 'SG' },
  { label: 'South Korea', code: 'KR', value: 'KR' },
  { label: 'Spain', code: 'ES', value: 'ES' },
  { label: 'Taiwan', code: 'TW', value: 'TW' },
  { label: 'Thailand', code: 'TH', value: 'TH' },
  { label: 'Turkey', code: 'TR', value: 'TR' },
  { label: 'United Arab Emirates', code: 'AE', value: 'AE' },
  { label: 'United Kingdom', code: 'GB', value: 'GB' },
  { label: 'United States', code: 'US', value: 'US' },
  { label: 'Vietnam', code: 'VN', value: 'VN' },
];

const instagramAudienceRegions = [
  { label: 'Afghanistan', value: 'AF' },
  { label: 'Angola', value: 'AO' },
  { label: 'Albania', value: 'AL' },
  { label: 'United Arab Emirates', value: 'AE' },
  { label: 'Argentina', value: 'AR' },
  { label: 'Armenia', value: 'AM' },
  { label: 'Australia', value: 'AU' },
  { label: 'Azerbaijan', value: 'AZ' },
  { label: 'Burundi', value: 'BI' },
  { label: 'Belgium', value: 'BE' },
  { label: 'Benin', value: 'BJ' },
  { label: 'Burkina Faso', value: 'BF' },
  { label: 'Bangladesh', value: 'BD' },
  { label: 'Bulgaria', value: 'BG' },
  { label: 'Bahamas', value: 'BS' },
  { label: 'Bosnia and Herzegovina', value: 'BA' },
  { label: 'Belarus', value: 'BY' },
  { label: 'Belize', value: 'BZ' },
  { label: 'Bolivia, Plurinational State of', value: 'BO' },
  { label: 'Brazil', value: 'BR' },
  { label: 'Barbados', value: 'BB' },
  { label: 'Bhutan', value: 'BT' },
  { label: 'Botswana', value: 'BW' },
  { label: 'Central African Republic', value: 'CF' },
  { label: 'Canada', value: 'CA' },
  { label: 'Chile', value: 'CL' },
  { label: 'China', value: 'CN' },
  { label: "Côte d'Ivoire", value: 'CI' },
  { label: 'Cameroon', value: 'CM' },
  { label: 'Congo, The Democratic Republic of the', value: 'CD' },
  { label: 'Congo', value: 'CG' },
  { label: 'Colombia', value: 'CO' },
  { label: 'Cabo Verde', value: 'CV' },
  { label: 'Costa Rica', value: 'CR' },
  { label: 'Cuba', value: 'CU' },
  { label: 'Cyprus', value: 'CY' },
  { label: 'Czechia', value: 'CZ' },
  { label: 'Djibouti', value: 'DJ' },
  { label: 'Denmark', value: 'DK' },
  { label: 'Dominican Republic', value: 'DO' },
  { label: 'Algeria', value: 'DZ' },
  { label: 'Ecuador', value: 'EC' },
  { label: 'Egypt', value: 'EG' },
  { label: 'Eritrea', value: 'ER' },
  { label: 'Spain', value: 'ES' },
  { label: 'Estonia', value: 'EE' },
  { label: 'Ethiopia', value: 'ET' },
  { label: 'Fiji', value: 'FJ' },
  { label: 'Gabon', value: 'GA' },
  { label: 'United Kingdom', value: 'GB' },
  { label: 'Georgia', value: 'GE' },
  { label: 'Ghana', value: 'GH' },
  { label: 'Guinea', value: 'GN' },
  { label: 'Guadeloupe', value: 'GP' },
  { label: 'Gambia', value: 'GM' },
  { label: 'Guinea-Bissau', value: 'GW' },
  { label: 'Greece', value: 'GR' },
  { label: 'French Guiana', value: 'GF' },
  { label: 'Guam', value: 'GU' },
  { label: 'Guyana', value: 'GY' },
  { label: 'Honduras', value: 'HN' },
  { label: 'Croatia', value: 'HR' },
  { label: 'Haiti', value: 'HT' },
  { label: 'Hungary', value: 'HU' },
  { label: 'Indonesia', value: 'ID' },
  { label: 'India', value: 'IN' },
  { label: 'Ireland', value: 'IE' },
  { label: 'Iran, Islamic Republic of', value: 'IR' },
  { label: 'Iraq', value: 'IQ' },
  { label: 'Iceland', value: 'IS' },
  { label: 'Israel', value: 'IL' },
  { label: 'Italy', value: 'IT' },
  { label: 'Jordan', value: 'JO' },
  { label: 'Japan', value: 'JP' },
  { label: 'Kenya', value: 'KE' },
  { label: 'Kyrgyzstan', value: 'KG' },
  { label: 'Cambodia', value: 'KH' },
  { label: 'Korea, Republic of', value: 'KR' },
  { label: 'Kuwait', value: 'KW' },
  { label: "Lao People's Democratic Republic", value: 'LA' },
  { label: 'Lebanon', value: 'LB' },
  { label: 'Liberia', value: 'LR' },
  { label: 'Libya', value: 'LY' },
  { label: 'Saint Lucia', value: 'LC' },
  { label: 'Sri Lanka', value: 'LK' },
  { label: 'Lesotho', value: 'LS' },
  { label: 'Lithuania', value: 'LT' },
  { label: 'Luxembourg', value: 'LU' },
  { label: 'Latvia', value: 'LV' },
  { label: 'Moldova, Republic of', value: 'MD' },
  { label: 'Madagascar', value: 'MG' },
  { label: 'Maldives', value: 'MV' },
  { label: 'Mexico', value: 'MX' },
  { label: 'North Macedonia', value: 'MK' },
  { label: 'Mali', value: 'ML' },
  { label: 'Malta', value: 'MT' },
  { label: 'Myanmar', value: 'MM' },
  { label: 'Montenegro', value: 'ME' },
  { label: 'Mozambique', value: 'MZ' },
  { label: 'Mauritania', value: 'MR' },
  { label: 'Martinique', value: 'MQ' },
  { label: 'Mauritius', value: 'MU' },
  { label: 'Malaysia', value: 'MY' },
  { label: 'Mayotte', value: 'YT' },
  { label: 'New Caledonia', value: 'NC' },
  { label: 'Nigeria', value: 'NG' },
  { label: 'Nicaragua', value: 'NI' },
  { label: 'Netherlands', value: 'NL' },
  { label: 'Norway', value: 'NO' },
  { label: 'Nepal', value: 'NP' },
  { label: 'New Zealand', value: 'NZ' },
  { label: 'Pakistan', value: 'PK' },
  { label: 'Peru', value: 'PE' },
  { label: 'Philippines', value: 'PH' },
  { label: 'Papua New Guinea', value: 'PG' },
  { label: 'Poland', value: 'PL' },
  { label: 'Puerto Rico', value: 'PR' },
  { label: 'Portugal', value: 'PT' },
  { label: 'Paraguay', value: 'PY' },
  { label: 'Palestine, State of', value: 'PS' },
  { label: 'French Polynesia', value: 'PF' },
  { label: 'Qatar', value: 'QA' },
  { label: 'Réunion', value: 'RE' },
  { label: 'Romania', value: 'RO' },
  { label: 'Russian Federation', value: 'RU' },
  { label: 'Saudi Arabia', value: 'SA' },
  { label: 'Sudan', value: 'SD' },
  { label: 'Senegal', value: 'SN' },
  { label: 'Singapore', value: 'SG' },
  { label: 'Sierra Leone', value: 'SL' },
  { label: 'El Salvador', value: 'SV' },
  { label: 'Somalia', value: 'SO' },
  { label: 'Serbia', value: 'RS' },
  { label: 'Sao Tome and Principe', value: 'ST' },
  { label: 'Suriname', value: 'SR' },
  { label: 'Slovakia', value: 'SK' },
  { label: 'Eswatini', value: 'SZ' },
  { label: 'Syrian Arab Republic', value: 'SY' },
  { label: 'Chad', value: 'TD' },
  { label: 'Togo', value: 'TG' },
  { label: 'Thailand', value: 'TH' },
  { label: 'Tajikistan', value: 'TJ' },
  { label: 'Turkmenistan', value: 'TM' },
  { label: 'Timor-Leste', value: 'TL' },
  { label: 'Trinidad and Tobago', value: 'TT' },
  { label: 'Tunisia', value: 'TN' },
  { label: 'Türkiye', value: 'TR' },
  { label: 'Taiwan, Province of China', value: 'TW' },
  { label: 'Tanzania, United Republic of', value: 'TZ' },
  { label: 'Uganda', value: 'UG' },
  { label: 'Ukraine', value: 'UA' },
  { label: 'Uruguay', value: 'UY' },
  { label: 'United States', value: 'US' },
  { label: 'Uzbekistan', value: 'UZ' },
  { label: 'Venezuela, Bolivarian Republic of', value: 'VE' },
  { label: 'Viet Nam', value: 'VN' },
  { label: 'Yemen', value: 'YE' },
  { label: 'Zambia', value: 'ZM' },
  { label: 'Switzerland', value: 'CH' },
  { label: 'Germany', value: 'DE' },
  { label: 'Mongolia', value: 'MN' },
  { label: 'Niger', value: 'NE' },
  { label: 'Rwanda', value: 'RW' },
  { label: 'Austria', value: 'AT' },
  { label: 'Jamaica', value: 'JM' },
  { label: 'Panama', value: 'PA' },
  { label: 'Bahrain', value: 'BH' },
  { label: 'Finland', value: 'FI' },
  { label: 'France', value: 'FR' },
  { label: 'Equatorial Guinea', value: 'GQ' },
  { label: 'Guatemala', value: 'GT' },
  { label: 'Kazakhstan', value: 'KZ' },
  { label: 'Morocco', value: 'MA' },
  { label: 'Malawi', value: 'MW' },
  { label: 'Namibia', value: 'NA' },
  { label: 'Oman', value: 'OM' },
  { label: 'Slovenia', value: 'SI' },
  { label: 'Sweden', value: 'SE' },
  { label: 'South Africa', value: 'ZA' },
  { label: 'Zimbabwe', value: 'ZW' },
  { label: 'Aruba', value: 'AW' },
  { label: 'Anguilla', value: 'AI' },
  { label: 'Andorra', value: 'AD' },
  { label: 'American Samoa', value: 'AS' },
  { label: 'Antigua and Barbuda', value: 'AG' },
  { label: 'Bermuda', value: 'BM' },
  { label: 'Brunei Darussalam', value: 'BN' },
  { label: 'Comoros', value: 'KM' },
  { label: 'Cayman Islands', value: 'KY' },
  { label: 'Dominica', value: 'DM' },
  { label: 'Falkland Islands (Malvinas)', value: 'FK' },
  { label: 'Faroe Islands', value: 'FO' },
  { label: 'Micronesia, Federated States of', value: 'FM' },
  { label: 'Guernsey', value: 'GG' },
  { label: 'Gibraltar', value: 'GI' },
  { label: 'Grenada', value: 'GD' },
  { label: 'Greenland', value: 'GL' },
  { label: 'Hong Kong', value: 'HK' },
  { label: 'Isle of Man', value: 'IM' },
  { label: 'Jersey', value: 'JE' },
  { label: 'Saint Kitts and Nevis', value: 'KN' },
  { label: 'Liechtenstein', value: 'LI' },
  { label: 'Macao', value: 'MO' },
  { label: 'Monaco', value: 'MC' },
  { label: 'Palau', value: 'PW' },
  { label: 'Saint Helena, Ascension and Tristan da Cunha', value: 'SH' },
  { label: 'Seychelles', value: 'SC' },
  { label: 'Turks and Caicos Islands', value: 'TC' },
  { label: 'Tonga', value: 'TO' },
  { label: 'Saint Vincent and the Grenadines', value: 'VC' },
  { label: 'Virgin Islands, British', value: 'VG' },
  { label: 'Vanuatu', value: 'VU' },
  { label: 'Samoa', value: 'WS' },
  { label: 'Virgin Islands, U.S.', value: 'VI' },
  { label: 'Cook Islands', value: 'CK' },
];

export const defaultInstagramAudienceRegions = [
  { label: 'United States', value: 'US' },
  { label: 'Canada', value: 'CA' },
  { label: 'United Kingdom', value: 'GB' },
  { label: 'Australia', value: 'AU' },
];

export const defaultTikTokLanguages = [
  { label: 'English', code: 'en', value: 'en' },
  { label: 'Spanish', code: 'es', value: 'es' },
  { label: 'Russian', code: 'ru', value: 'ru' },
];

const tiktokLanguages = [
  { label: 'Arabic', code: 'ar', value: 'ar' },
  { label: 'German', code: 'de', value: 'de' },
  { label: 'English', code: 'en', value: 'en' },
  { label: 'Spanish', code: 'es', value: 'es' },
  { label: 'French', code: 'fr', value: 'fr' },
  { label: 'Indonesian', code: 'id', value: 'id' },
  { label: 'Italian', code: 'it', value: 'it' },
  { label: 'Japanese', code: 'ja', value: 'ja' },
  { label: 'Korean', code: 'ko', value: 'ko' },
  { label: 'Malay', code: 'ms', value: 'ms' },
  { label: 'Portuguese', code: 'pt', value: 'pt' },
  { label: 'Thai', code: 'th', value: 'th' },
  { label: 'Tagalog', code: 'tl', value: 'tl' },
  { label: 'Turkish', code: 'tr', value: 'tr' },
  { label: 'Vietnamese', code: 'vi', value: 'vi' },
];

export const tikTokDiscoveryAgeOptions = [
  { value: '', label: 'All Ages', minAge: null, maxAge: null },
  { value: '18-24', label: '18-24', minAge: 18, maxAge: 24 },
  { value: '25-34', label: '25-34', minAge: 25, maxAge: 34 },
  { value: '35-44', label: '35-44', minAge: 35, maxAge: 44 },
  { value: '45-54', label: '45-54', minAge: 45, maxAge: 54 },
  { value: '55+', label: '55+', minAge: 55, maxAge: null },
];

const tikTokDiscoveryGenderRatioOptions = [
  { value: 'FEMALE_50', label: 'Female is over 50%' },
  { value: 'MALE_50', label: 'Male is over 50%' },
  { value: 'FEMALE_60', label: 'Female is over 60%' },
  { value: 'MALE_60', label: 'Male is over 60%' },
  { value: 'FEMALE_70', label: 'Female is over 70%' },
  { value: 'MALE_70', label: 'Male is over 70%' },
];

const tikTokUSStates = [
  { value: 'Alabama', label: 'Alabama' },
  { value: 'Alaska', label: 'Alaska' },
  { value: 'Arizona', label: 'Arizona' },
  { value: 'Arkansas', label: 'Arkansas' },
  { value: 'California', label: 'California' },
  { value: 'Colorado', label: 'Colorado' },
  { value: 'Connecticut', label: 'Connecticut' },
  { value: 'Delaware', label: 'Delaware' },
  { value: 'Florida', label: 'Florida' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Hawaii', label: 'Hawaii' },
  { value: 'Idaho', label: 'Idaho' },
  { value: 'Illinois', label: 'Illinois' },
  { value: 'Indiana', label: 'Indiana' },
  { value: 'Iowa', label: 'Iowa' },
  { value: 'Kansas', label: 'Kansas' },
  { value: 'Kentucky', label: 'Kentucky' },
  { value: 'Louisiana', label: 'Louisiana' },
  { value: 'Maine', label: 'Maine' },
  { value: 'Maryland', label: 'Maryland' },
  { value: 'Massachusetts', label: 'Massachusetts' },
  { value: 'Michigan', label: 'Michigan' },
  { value: 'Minnesota', label: 'Minnesota' },
  { value: 'Mississippi', label: 'Mississippi' },
  { value: 'Missouri', label: 'Missouri' },
  { value: 'Montana', label: 'Montana' },
  { value: 'Nebraska', label: 'Nebraska' },
  { value: 'Nevada', label: 'Nevada' },
  { value: 'New Hampshire', label: 'New Hampshire' },
  { value: 'New Jersey', label: 'New Jersey' },
  { value: 'New Mexico', label: 'New Mexico' },
  { value: 'New York', label: 'New York' },
  { value: 'North Carolina', label: 'North Carolina' },
  { value: 'North Dakota', label: 'North Dakota' },
  { value: 'Ohio', label: 'Ohio' },
  { value: 'Oklahoma', label: 'Oklahoma' },
  { value: 'Oregon', label: 'Oregon' },
  { value: 'Pennsylvania', label: 'Pennsylvania' },
  { value: 'Rhode Island', label: 'Rhode Island' },
  { value: 'South Carolina', label: 'South Carolina' },
  { value: 'South Dakota', label: 'South Dakota' },
  { value: 'Tennessee', label: 'Tennessee' },
  { value: 'Texas', label: 'Texas' },
  { value: 'Utah', label: 'Utah' },
  { value: 'Vermont', label: 'Vermont' },
  { value: 'Virginia', label: 'Virginia' },
  { value: 'Washington', label: 'Washington' },
  { value: 'West Virginia', label: 'West Virginia' },
  { value: 'Wisconsin', label: 'Wisconsin' },
  { value: 'Wyoming', label: 'Wyoming' },
];

const tikTokUSLocations = [
  { value: 'US', label: 'United States', hasFlag: true, children: tikTokUSStates },
];

const tikTokOtherCountries = [
  { value: 'AR', label: 'Argentina', code: 'AR' },
  { value: 'AU', label: 'Australia', code: 'AU' },
  { value: 'BR', label: 'Brazil', code: 'BR' },
  { value: 'CA', label: 'Canada', code: 'CA' },
  { value: 'CO', label: 'Colombia', code: 'CO' },
  { value: 'EG', label: 'Egypt', code: 'EG' },
  { value: 'FR', label: 'France', code: 'FR' },
  { value: 'DE', label: 'Germany', code: 'DE' },
  { value: 'ID', label: 'Indonesia', code: 'ID' },
  { value: 'IL', label: 'Israel', code: 'IL' },
  { value: 'IT', label: 'Italy', code: 'IT' },
  { value: 'JP', label: 'Japan', code: 'JP' },
  { value: 'MY', label: 'Malaysia', code: 'MY' },
  { value: 'MX', label: 'Mexico', code: 'MX' },
  { value: 'PH', label: 'Philippines', code: 'PH' },
  { value: 'SA', label: 'Saudi Arabia', code: 'SA' },
  { value: 'SG', label: 'Singapore', code: 'SG' },
  { value: 'KR', label: 'South Korea', code: 'KR' },
  { value: 'ES', label: 'Spain', code: 'ES' },
  { value: 'TW', label: 'Taiwan', code: 'TW' },
  { value: 'TH', label: 'Thailand', code: 'TH' },
  { value: 'TR', label: 'Turkey', code: 'TR' },
  { value: 'AE', label: 'United Arab Emirates', code: 'AE' },
  { value: 'GB', label: 'United Kingdom', code: 'GB' },
  { value: 'VN', label: 'Vietnam', code: 'VN' },
];

export const igCmSortOrderOptions = [
  {
    value: 'DESC',
    label: 'Descending',
    icon: 'arrow-down',
  },
  {
    value: 'ASC',
    label: 'Ascending',
    icon: 'arrow-up',
    disabled: true,
    tooltip:
      'Ascending option is only available when Followers, Interests, Keywords, Location City, Mentions, Competitive Mentions, Posts Published Within and Visual Filters are applied.',
  },
];

export const sortDetails = {
  YOUR_CREATORS: {
    options: {
      MULTIPLE_CHANNELS: [
        { label: 'Posts Feat. You', value: 'TOTAL_POSTS_FEAT_YOU' },
        { label: 'Recently Added', value: 'ADDED_TIME' },
        { label: 'Recently Published', value: 'LATEST_POST' },
        { label: 'Instagram Followers', value: 'INSTAGRAM_FOLLOWERS' },
        { label: 'TikTok Followers', value: 'TIKTOK_FOLLOWERS' },
        { label: 'YouTube Subscribers', value: 'YOUTUBE_SUBSCRIBERS' },
      ],
      INSTAGRAM: [
        { label: 'Recently Added', value: 'ADDED_TIME' },
        { label: 'Posts Feat. You', value: 'TOTAL_POSTS_FEAT_YOU' },
        { label: 'Recently Published', value: 'PUBLISH_TIME' },
        { label: 'Followers', value: 'INSTAGRAM_FOLLOWERS' },
        { label: 'Avg. Engagement Rate', value: 'AVG_ENGAGEMENT_RATE' },
        { label: 'Avg. Engagements', value: 'AVG_ENGAGEMENT' },
        { label: 'Avg. EMV', value: 'AVG_EMV' },
        { label: 'Avg. Est. Reach', value: 'AVG_REACH' },
      ],
      INSTAGRAM_FEED_ONLY: [
        { label: 'Recently Added', value: 'ADDED_TIME' },
        { label: 'Posts Feat. You', value: 'TOTAL_POSTS_FEAT_YOU' },
        { label: 'Recently Published', value: 'PUBLISH_TIME' },
        { label: 'Followers', value: 'INSTAGRAM_FOLLOWERS' },
        { label: 'Avg. Engagement Rate', value: 'AVG_ENGAGEMENT_RATE' },
        { label: 'Avg. Engagements', value: 'AVG_ENGAGEMENT' },
        { label: 'Avg. EMV', value: 'AVG_EMV' },
        { label: 'Avg. Est. Reach', value: 'AVG_REACH' },
      ],
      INSTAGRAM_STORIES_ONLY: [
        { label: 'Posts Feat. You', value: 'TOTAL_POSTS_FEAT_YOU' },
        { label: 'Recently Added', value: 'ADDED_TIME' },
        { label: 'Recently Published', value: 'PUBLISH_TIME' },
        { label: 'Avg. Completion Rate', value: 'AVG_COMPLETION_RATE' },
        { label: 'Avg. EMV', value: 'AVG_EMV' },
        { label: 'Avg. Exit Rate', value: 'AVG_EXIT_RATE' },
        { label: 'Avg. Views', value: 'AVG_VIDEO_VIEWS' },
        { label: 'EMV', value: 'TOTAL_EMV' },
        { label: 'Views', value: 'VIDEO_VIEWS' },
      ],
      TIKTOK: [
        { label: 'Recently Added', value: 'ADDED_TIME' },
        { label: 'Posts Feat. You', value: 'TOTAL_POSTS_FEAT_YOU' },
        { label: 'Recently Published', value: 'PUBLISH_TIME' },
        { label: 'Followers', value: 'TIKTOK_FOLLOWERS' },
        { label: 'Avg. Engagement Rate', value: 'AVG_ENGAGEMENT_RATE' },
        { label: 'Avg. Engagements', value: 'AVG_ENGAGEMENT' },
        { label: 'Avg. EMV', value: 'AVG_EMV' },
        { label: 'Avg. Video Views', value: 'AVG_VIDEO_VIEWS' },
        { label: 'Median Views', value: 'MEDIAN_VIEWS' },
      ],
      YOUTUBE: [
        { label: 'Recently Added', value: 'ADDED_TIME' },
        { label: 'Posts Feat. You', value: 'TOTAL_POSTS_FEAT_YOU' },
        { label: 'Recently Published', value: 'PUBLISH_TIME' },
        { label: 'Subscribers', value: 'YOUTUBE_SUBSCRIBERS' },
        { label: 'Avg. Engagement Rate', value: 'AVG_ENGAGEMENT_RATE' },
        { label: 'Avg. Engagements', value: 'AVG_ENGAGEMENT' },
        { label: 'Avg. EMV', value: 'AVG_EMV' },
        { label: 'Avg. Views', value: 'AVG_VIDEO_VIEWS' },
      ],
    },
    default: {
      sortOrder: 'DESC',
      selected: { label: 'Recently Published', value: 'LATEST_POST' },
    },
  },
  DISCOVERY: {
    options: {
      TIKTOK: [
        { label: 'Recommended', value: 'RELEVANCE' },
        { label: 'Followers', value: 'FOLLOWERS' },
        { label: 'Engagement Rate', value: 'ENGAGEMENT_RATE' },
        { label: 'Median Views', value: 'MEDIAN_VIEWS' },
      ],
      INSTAGRAM: [
        { label: 'Avg. Engagements', value: 'instagram.engagements' },
        { label: 'Avg. Engagement Rate', value: 'instagram.engagement_rate' },
        { label: 'Followers', value: 'followers' },
        { label: 'Recommended', value: 'relevance' },
      ],
    },
    default: {
      TIKTOK: {
        sortOrder: 'DESC',
        selected: { label: 'Recommended', value: 'RELEVANCE' },
      },
      INSTAGRAM: {
        sortOrder: 'DESC',
        selected: { label: 'Avg. Engagements', value: 'instagram.engagements' },
      },
    },
  },
  DELIVERABLES: {
    options: {
      ALL: [
        { label: 'Creator', value: 'CREATOR_HANDLE' },
        { label: 'Deliverables', value: 'DELIVERABLES' },
        { label: 'Posts Published', value: 'TOTAL_POSTS_FT_YOU' },
        { label: 'Last Post Ft. You', value: 'LATEST_POST_FT_YOU' },
        { label: 'Status', value: 'STATUS' },
      ],
    },
    default: {
      sortOrder: 'DESC',
      selected: { label: 'Last Post Ft. You', value: 'LATEST_POST_FT_YOU' },
    },
  },
  PAYMENTS: {
    options: {
      ALL: [
        { label: 'Email', value: 'email' },
        { label: 'Last Updated', value: 'updatedAt' },
        { label: 'Name', value: 'name' },
      ],
    },
    default: {
      sortOrder: 'DESC',
      selected: { label: 'Last Updated', value: 'updatedAt' },
    },
  },
  INFLUENTIAL_FANS: {
    options: {
      MOST_INFLUENTIAL: {
        label: 'Recommended',
        value: 'MOST_INFLUENTIAL',
      },
      DATE: {
        label: 'Recently Published',
        value: 'DATE',
      },
      FOLLOWERS: {
        label: 'Followers',
        value: 'FOLLOWERS',
      },
      MENTIONS: {
        label: 'Posts Feat. You',
        value: 'MENTIONS',
      },
      AVG_ENGAGEMENTS: {
        label: 'Avg. Engagements',
        value: 'AVG_ENGAGEMENTS',
      },
      TOTAL_VIDEO_VIEWS: {
        label: 'Video Views',
        value: 'TOTAL_VIDEO_VIEWS',
      },
      AVG_ENGAGEMENT_RATE: {
        label: 'Avg. Engagement Rate',
        value: 'AVG_ENGAGEMENT_RATE',
      },
    },
    default: {
      sortOrder: 'DESC',
      selected: { label: 'Recommended', value: 'MOST_INFLUENTIAL' },
    },
  },
};

export const TTCM_STATUS_OPTIONS = Object.freeze({
  IN: 'IN',
  NOT_IN: 'NOT_IN',
  INVITED: 'INVITED',
});

const CREATOR_FILTER_OPTIONS = Object.freeze({
  restrictToPlatforms: [
    { label: 'TikTok', value: 'TIKTOK', icon: 'tiktok-color' },
    { label: 'Instagram', value: 'INSTAGRAM', icon: 'instagram-color' },
    { label: 'YouTube', value: 'YOUTUBE', icon: 'youtube-color' },
  ],
  tagFilterType: [
    {
      label: 'Match All Selected Tags',
      value: 'AND',
    },
    {
      label: 'Match Any Selected Tags',
      value: 'OR',
    },
  ],
  hasMentionedBrand: [
    { label: 'Mentioned Me', value: 'MENTIONED_ME' },
    { label: 'Has Not Mentioned Me', value: 'HAS_NOT_MENTIONED_ME' },
  ],
  competitiveMentionsFilterType: [
    { label: 'Competitors', value: 'competitors' },
    { label: 'Competitor Tags', value: 'competitorTags' },
  ],
  connectionStatus: [
    {
      label: 'Not Connected',
      value: 'NOT_INVITED',
      tooltip: 'No successful connection has been made',
    },
    {
      label: 'Invite Sent',
      value: ['INVITATION_SENT', 'LAPSED'],
      tooltip: "You've sent an email connection invite",
    },
    {
      label: 'Connected',
      value: 'ACCEPTED',
      tooltip: 'The creator has accepted your connection request',
    },
    {
      label: 'Revoked',
      value: 'REVOKED',
      tooltip: 'The connection was revoked by the creator',
    },
  ],
  instagramPostType: [
    { label: 'Feed & Reels', value: 'FEED_POSTS', channel: 'instagram' },
    { label: 'Stories', value: 'STORY', channel: 'instagram-story' },
  ],
  youtubePostType: [
    { label: 'Videos', value: 'VIDEO', channel: 'youtube' },
    { label: 'Shorts', value: 'SHORTS', channel: 'youtube-short' },
  ],
  instagramInterests: discoveryInterests,
  instagramIsBusiness: [
    { label: 'Professional', value: true },
    { label: 'Personal', value: false },
  ],
  tiktokTTCMStatus: [
    { label: 'Registered', value: 'IN' },
    { label: 'Not Registered', value: 'NOT_IN' },
    { label: 'Pending', value: 'INVITED' },
  ],
  tiktokRegions,
  tiktokAudienceAge: [
    { label: 'All Ages', value: 'ALL_AGES' },
    { label: '18-24', value: '18-24' },
    { label: '25-34', value: '25-34' },
    { label: '35+', value: '35+' },
  ],
  tiktokAudienceGender: [
    { label: 'All Genders', value: 'ALL_GENDERS' },
    { label: 'Male', value: 'Male' },
    { label: 'Female', value: 'Female' },
  ],
  tiktokOrganicInterests: [],
  tiktokPaidInterests: [],
  youtubeFollowersGreaterThan: null,
  youtubeFollowersLessThan: null,
  youtubeVideoCountGreaterThan: null,
  youtubeVideoCountLessThan: null,
  youtubeViewsGreaterThan: null,
  youtubeViewsLessThan: null,
});

const CREATOR_FILTER_DEFAULTS = Object.freeze({
  restrictToPlatforms: [],
  datePublished: {
    preset: 'ALL_TIME',
  },
  tagFilterType: 'OR',
  tags: [],
  tagsSearch: '',
  creatorLocations: [],
  creatorLocationsSearch: '',
  hasMentionedBrand: [],
  instagramPostType: [],
  instagramConnectionStatus: [],
  instagramFollowersGreaterThan: null,
  instagramFollowersLessThan: null,
  instagramInterests: [],
  instagramInterestsSearch: '',
  instagramIsBusiness: [],
  tiktokConnectionStatus: [],
  tiktokFollowersGreaterThan: null,
  tiktokFollowersLessThan: null,
  tiktokEngagementRateRange: { min: null, max: null },
  tiktokMedianViewsRange: { min: null, max: null },
  tiktokTTCMStatus: [],
  tiktokAudienceRegions: [],
  tiktokAudienceAge: 'ALL_AGES',
  tiktokAudienceGender: 'ALL_GENDERS',
  tiktokOrganicInterests: [],
  tiktokOrganicInterestsSearch: '',
  tiktokPaidInterests: [],
  tiktokPaidInterestsSearch: '',
  tiktokCreatorRatesRange: { min: null, max: null },
  tiktokCreatorRatesCurrency: 'USD',
  youtubePostType: [],
  youtubeFollowersGreaterThan: null,
  youtubeFollowersLessThan: null,
  youtubeVideoCountGreaterThan: null,
  youtubeVideoCountLessThan: null,
  youtubeViewsGreaterThan: null,
  youtubeViewsLessThan: null,
  competitiveMentions: [],
  competitiveMentionsFilterType: 'competitors',
  hasMentionedCompetitor: [],
  competitiveMentionsSearch: '',
});

const invalidTrolleyCountries = ['AQ', 'AX', 'BV', 'IO', 'CC', 'HM', 'KP', 'UM', 'NR', 'EH'];

// renames to match Trolley's list of countries
const payeesCountryLabelOverrides = {
  CI: 'Ivory Coast', // shared list uses "Côte d'Ivoire"
  VA: 'Vatican City', // shared list uses "Holy See"
};

export const payeesCountries = countries
  .filter((country) => !invalidTrolleyCountries.includes(country.value))
  .map((country) => ({
    value: country.value,
    label: `${getCountryFlag(country.value)} ${payeesCountryLabelOverrides[country.value] ?? country.label}`,
  }));

const suggestedCountries = Object.freeze([
  { value: 'CA', label: `${getCountryFlag('CA')} Canada` },
  { value: 'US', label: `${getCountryFlag('US')} United States` },
  { value: 'GB', label: `${getCountryFlag('GB')} United Kingdom` },
  { value: 'FR', label: `${getCountryFlag('FR')} France` },
]);

export const PayeeStatus = Object.freeze({
  ACTIVE: 'active',
  INCOMPLETE: 'incomplete',
  DISABLED: 'disabled',
  BLOCKED: 'blocked',
  ARCHIVED: 'archived',
  SUSPENDED: 'suspended',
});

const payeeStatusTooltips = Object.freeze({
  active: 'The creator is fully set up and can receive payments',
  archived:
    'The creator was removed from your active list. Archived profiles can be unarchived if needed',
  blocked:
    "This payee was blocked for compliance reasons and can't be paid. Their details remain on file.",
  deactivated:
    "You've deactivated this payee, so they can't be paid until you reactivate them. You can reactivate them anytime.",
  incomplete:
    "The Creator hasn't completed their profile setup, so they can't be paid yet. Encourage them to double-check their personal, banking, and tax details.",
  suspended:
    "You've suspended this payee, so they can't be paid or linked to another account. Their profile details remain visible.",
});

const PAYEES_FILTER_OPTIONS = Object.freeze({
  status: [
    {
      label: 'Active',
      value: PayeeStatus.ACTIVE,
      color: `var(--color-filter-green)`,
      tooltipIcon: payeeStatusTooltips.active,
    },
    {
      label: 'Incomplete',
      value: PayeeStatus.INCOMPLETE,
      color: `var(--color-filter-yellow)`,
      tooltipIcon: payeeStatusTooltips.incomplete,
    },
    {
      label: 'Deactivated',
      value: PayeeStatus.DISABLED,
      color: `var(--color-filter-grey)`,
      tooltipIcon: payeeStatusTooltips.deactivated,
    },
    {
      label: 'Blocked',
      value: PayeeStatus.BLOCKED,
      color: `var(--color-filter-red)`,
      tooltipIcon: payeeStatusTooltips.blocked,
    },
    {
      label: 'Suspended',
      value: PayeeStatus.SUSPENDED,
      color: `var(--color-filter-red)`,
      tooltipIcon: payeeStatusTooltips.suspended,
    },
  ],
  payoutMethod: [
    { label: 'Bank Transfer', value: 'bank-transfer' },
    { label: 'PayPal', value: 'paypal' },
  ],
  countries: payeesCountries,
  suggestedCountries,
});

const PAYEES_FILTER_DEFAULTS = Object.freeze({
  status: [],
  payoutMethod: [],
  lastUpdated: {
    preset: 'ALL_TIME',
    presetObject: { value: 'ALL_TIME', label: 'All Time' },
    range: undefined,
  },
  countries: [],
  countriesSearch: '',
});

// Values must match the `payment_status` metadata-tag values Trolley stores per batch.
// Kept in sync with the keys of `paymentStatusChipDisplay` (declared later in this file);
// the filter panel reads `paymentStatusChipDisplay[value].severity` to color the option dot.
const PAYMENT_HISTORY_FILTER_OPTIONS = Object.freeze({
  paymentStatus: [
    { label: 'Pending', value: 'pending' },
    { label: 'Processing', value: 'processing' },
    { label: 'Processed', value: 'processed' },
    { label: 'Failed', value: 'failed' },
    { label: 'Returned', value: 'returned' },
  ],
});

const PAYMENT_HISTORY_FILTER_DEFAULTS = Object.freeze({
  paymentStatus: undefined,
  campaign: undefined,
  payee: undefined,
  lastUpdated: {
    preset: 'ALL_TIME',
    presetObject: { value: 'ALL_TIME', label: 'All Time' },
    range: undefined,
  },
});

export const filterDetails = {
  YOUR_CREATORS: {
    scope: 'yourCreators',
    options: CREATOR_FILTER_OPTIONS,
    default: CREATOR_FILTER_DEFAULTS,
    noRemember: false,
  },
  CREATORS_WIZARD: {
    scope: 'creatorsWizard',
    options: CREATOR_FILTER_OPTIONS,
    default: CREATOR_FILTER_DEFAULTS,
    noRemember: true,
  },
  TIKTOK_DISCOVERY: {
    scope: 'tikTokDiscovery',
    options: {
      age: tikTokDiscoveryAgeOptions,
      engagementRate: tikTokDiscoveryEngagementRateOptions,
      genderRatio: tikTokDiscoveryGenderRatioOptions,
      languages: tiktokLanguages,
      creatorCountryCode: tiktokRegions,
      audienceCountryCode: tiktokRegions,
      creatorLocation: tikTokOtherCountries,
      creatorUSLocation: tikTokUSLocations,
    },
    default: {
      age: undefined,
      keywords: '',
      engagementRate: undefined,
      genderRatio: undefined,
      interests: undefined,
      languages: [],
      languagesSearch: '',
      engagementRateRange: { min: null, max: null },
      followersRange: { min: null, max: null },
      avgViewsRange: { min: null, max: null },
      medianViewsRange: { min: null, max: null },
      creatorCountryCode: undefined,
      audienceCountryCode: undefined,
      audienceCountryCodeSearch: '',
      organicInterests: undefined,
      organicInterestsSearch: '',
      paidInterests: undefined,
      paidInterestsSearch: '',
      creatorRatesRange: { min: null, max: null },
      creatorRatesCurrency: 'USD',
      creatorLocation: undefined,
      creatorUSLocation: [],
      creatorLocationSearch: '',
      creatorUSLocationSearch: '',
      locationType: 'US',
    },
  },
  INSTAGRAM_DISCOVERY: {
    scope: 'instagramDiscovery',
    options: {
      interests: discoveryInterests,
      hasMentionedBrand: [
        { label: 'Mentioned Me', value: 'MENTIONED_ME' },
        { label: 'Has Not Mentioned Me', value: 'HAS_NOT_MENTIONED_ME' },
      ],
      keywordInclusion: [
        { label: 'Have Used', value: true },
        { label: "Haven't Used", value: false },
      ],
      keywordLocation: [
        { label: 'Anywhere', value: 'anywhere' },
        { label: 'Caption', value: 'caption' },
        { label: 'Instagram Bio', value: 'bio' },
      ],
      competitiveMentionsFilterType: [
        { label: 'Competitors', value: 'competitors' },
        { label: 'Competitor Tags', value: 'competitorTags' },
      ],
      audienceCountryCode: instagramAudienceRegions,
      age: instagramDiscoveryAgeOptions,
      audienceAge: instagramDiscoveryAgeOptions,
      gender: instagramDiscoveryGenderOptions,
      audienceGender: instagramDiscoveryGenderOptions,
      reelsInteractionRate: instagramDiscoveryReelsInteractionOptions,
      engagedAccounts: instagramDiscoveryEngagedAccountsOptions,
    },
    default: {
      followersRange: { min: null, max: null },
      interests: [],
      interestsSearch: '',
      countries: [],
      locations: [],
      locationSearch: '',
      hasMentionedBrand: [],
      hasPostedBetween: {
        preset: 'ALL_TIME',
      },
      keywords: [{ shouldInclude: true, keyword: '', location: 'anywhere' }],
      visualFeatures: undefined,
      competitiveMentions: [],
      competitiveMentionsFilterType: 'competitors',
      hasMentionedCompetitor: [],
      competitiveMentionsSearch: '',
      audienceCountryCode: undefined,
      age: [],
      audienceAge: [],
      gender: [],
      audienceGender: [],
      engagedAccounts: { min: null, max: null },
      instagram: undefined,
    },
  },
  PAYEES: {
    scope: 'payees',
    options: PAYEES_FILTER_OPTIONS,
    default: PAYEES_FILTER_DEFAULTS,
    noRemember: false,
  },
  PAYMENT_HISTORY: {
    scope: 'paymentHistory',
    options: PAYMENT_HISTORY_FILTER_OPTIONS,
    default: PAYMENT_HISTORY_FILTER_DEFAULTS,
    noRemember: false,
  },
};

export const IGCM_ONLY_FILTERS = [
  'age',
  'gender',
  'audienceAge',
  'audienceGender',
  'audienceCountryCode',
  'engagedAccounts',
  'reelsInteractionRate',
];

export const INDEX_ONLY_FILTERS = [
  'hasMentionedBrand',
  'hasPostedBetween',
  'visualFeatures',
  'competitiveMentions',
  'hasMentionedCompetitor',
];

export const tiktokFilterInputLimit = {
  minFollowers: 100,
  maxFollowers: 2_000_000_000, // TTO throws error for any integer exceed int32 range
  minAvgViews: 1,
  maxAvgViews: 2_000_000_000, // TTO throws error for any integer exceed int32 range
  minEngagementRate: 1,
  maxEngagementRate: 100,
  minCreatorRates: 1,
  maxCreatorRates: 100_000_000_000,
  minMedianViews: 1,
  maxMedianViews: 2_000_000_000, // TTO throws error for any integer exceed int32 range
};

export const currencies = [
  { label: 'United States Dollar (USD)', value: 'USD' },
  { label: 'British Pound Sterling (GBP)', value: 'GBP' },
  { label: 'Australian Dollar (AUD)', value: 'AUD' },
  { label: 'Canadian Dollar (CAD)', value: 'CAD' },
  { label: 'Euro (EUR)', value: 'EUR' },
  { label: 'Swedish Krona (SEK)', value: 'SEK' },
  { label: 'Danish Krone (DKK)', value: 'DKK' },
  { label: 'Swiss Franc (CHF)', value: 'CHF' },
  { label: 'UAE Dirham (AED)', value: 'AED' },
  { label: 'Brazilian Real (BRL)', value: 'BRL' },
  { label: 'Chinese Yuan (CNY)', value: 'CNY' },
  { label: 'Indian Rupee (INR)', value: 'INR' },
  { label: 'Japanese Yen (JPY)', value: 'JPY' },
  { label: 'Singapore Dollar (SGD)', value: 'SGD' },
  { label: 'Polish Złoty (PLN)', value: 'PLN' },
  { label: 'Hong Kong Dollar (HKD)', value: 'HKD' },
  { label: 'Hungarian Forint (HUF)', value: 'HUF' },
  { label: 'Icelandic Króna (ISK)', value: 'ISK' },
  { label: 'Israeli New Shekel (ILS)', value: 'ILS' },
  { label: 'Kuwaiti Dinar (KWD)', value: 'KWD' },
  { label: 'Lebanese Pound (LBP)', value: 'LBP' },
  { label: 'Mexican Peso (MXN)', value: 'MXN' },
  { label: 'South African Rand (ZAR)', value: 'ZAR' },
  { label: 'South Korean Won (KRW)', value: 'KRW' },
  { label: 'New Taiwan Dollar (TWD)', value: 'TWD' },
  { label: 'Ukrainian Hryvnia (UAH)', value: 'UAH' },
  { label: 'Colombian Peso (COP)', value: 'COP' },
  { label: 'Dominican Peso (DOP)', value: 'DOP' },
];

export const tiktokInterestsTypeOptions = [
  {
    value: 'organicInterests',
    title: 'Organic',
    tooltip: 'Shows the creator’s main interest based on their unpaid or natural TikTok content',
  },
  {
    value: 'paidInterests',
    title: 'Paid',
    tooltip: 'Shows the creator’s commercial interest based on branded or ad content they produce',
  },
];

export const instagramFilterInputLimit = {
  minFollowers: 1000,
  maxFollowers: 1000000000,
};

export const instagramDiscoveryFollowersOptions = {
  min: [0, 10000, 25000, 50000, 75000, 100000],
  max: [10000, 25000, 50000, 75000, 100000],
};

export const igcmReconnectionBannerMessage =
  'We’ve updated our Instagram integration for the new Creator Marketplace. You need to reconnect your Instagram account in Settings and enroll your business in the new Instagram Creator Marketplace to continue accessing creator insights and collaboration features.';

export const igcmNotEnrolledDiscoveryBannerMessage =
  'Gain access to more creators, discovery filters & creator data. First, go to Meta Business Suite to enrol your brand into Instagram Creator Marketplace then reconnect your Instagram platform account in Dash Social.';

export const igcmNotEnrolledProfileBannerMessage =
  'Gain access to more creators, discovery filters & creator data. First, go to Meta Business Suite to enrol your brand into Instagram Creator Marketplace then reconnect your IG platform account in Dash Social.';

export const igcmNotEnrolledDiscoverySetUpLaterMessage =
  'You can reconnect your Instagram Account at any time after enrolling in Instagram Creator Marketplace by going to Settings > Platform Connections.';

export const igcmDiscoveryLearnMoreUrl =
  'https://help.dashsocial.com/hc/en-us/articles/43038502709901';

export const inviteStatusDetails = {
  NOT_INVITED: {
    label: 'Not Invited',
    value: 'NOT_INVITED',
    severity: 'neutral',
  },
  INVITATION_SENT: {
    label: 'Invite Sent',
    value: 'INVITATION_SENT',
    severity: 'statusNeutral',
    tooltip: 'This account has not yet accepted your invitation to connect.',
  },
  ACCEPTED: {
    label: 'Connected',
    value: 'ACCEPTED',
    severity: 'positive',
    tooltip: 'This account has accepted your invite and is connected.',
  },
  LAPSED: {
    label: 'Expired',
    value: 'LAPSED',
    severity: 'statusNeutral',
    tooltip: 'The invite sent to this account has not been accepted in over 14 days.',
  },
  REVOKED: {
    label: 'Revoked',
    value: 'REVOKED',
    severity: 'statusNeutral',
    tooltip:
      'This connection has been revoked by the creator. Select analytics and Stories will not import until they reconnect.',
  },
  DISCONNECTED: {
    label: 'Disconnected',
    value: 'DISCONNECTED',
    severity: 'warning',
    tooltip:
      'This account has been disconnected. Select analytics and Stories will not import until they reconnect.',
  },
};

export const emptyStateDetails = {
  NO_SEARCH: {
    value: 'NO_SEARCH',
    variant: 'pageEmpty',
    icon: 'search',
    title: 'No results found',
    message:
      'You may want to try different keywords, checking for typos, or adjusting your filters',
  },
  NO_FILTER: {
    value: 'NO_FILTER',
    variant: 'pageEmpty',
    icon: 'alert-triangle',
    title: 'No Creators Match Your Filters',
    message: 'Adjust your filters to view more creators',
  },
  NO_FILTER_AI: {
    value: 'NO_FILTER_AI',
    variant: 'sectionEmpty',
    icon: 'vision-ai',
    title: 'No Creators Match Your Filters',
    message: 'Adjust your prompt for different filters',
  },
  NO_CREATORS: {
    value: 'NO_CREATORS',
    variant: 'pageEmpty',
    icon: 'user',
    title: 'No Creators Yet',
    message: 'Discover and add new creators that you want to work with',
  },
  NO_POSTS: {
    value: 'NO_POSTS',
    variant: 'sectionEmpty',
    title: 'Tagged Content Will Display Here',
    icon: 'at-sign',
    message: 'This creator hasn’t tagged or @mentioned your brand during the selected date range.',
  },
  NO_COLLABORATIONS: {
    value: 'NO_COLLABORATIONS',
    variant: 'sectionEmpty',
    title: 'Collaboration Content Will Display Here',
    icon: 'at-sign',
    message: 'This creator hasn’t collaborated with your brand during the selected date range.',
  },
  NO_POSTS_FULL_FEED: {
    value: 'NO_POSTS_FULL_FEED',
    variant: 'sectionEmpty',
    title: 'No Posts Yet',
    message:
      'This creator hasn’t published any posts. Their posts will appear here once they start sharing content.',
  },
  NO_STORIES: {
    value: 'NO_STORIES',
    variant: 'sectionEmpty',
    title: 'Stories Featuring You Will Display Here',
    message:
      'This creator hasn’t @mentioned your brand in an Instagram story during the selected date range. Switch to Posts or adjust your date range to view more.',
  },
  NO_POSTS_HAS_STORIES: {
    value: 'NO_POSTS_HAS_STORIES',
    variant: 'sectionEmpty',
    title: 'Posts Featuring You Will Display Here',
    message:
      'This creator hasn’t tagged or @mentioned your brand in a post during the selected date range. Switch to Stories or adjust your date range to view more.',
  },
  NO_HANDLE: {
    value: 'NO_HANDLE',
    variant: 'pageEmpty',
    icon: 'search',
    title: 'No TikTok Users Found',
    message: 'Try a different handle, checking for typos or adjusting your filters',
  },
  RATE_LIMITED: {
    value: 'RATE_LIMITED',
    variant: 'pageError',
    icon: 'alert-triangle',
    title: 'Try Again',
    message:
      "There was a delay in retrieving creators' data, possibly due to high traffic or API issues. Please refresh the page in a few moments.",
  },
  GENERIC: {
    value: 'GENERIC',
    variant: 'pageError',
    icon: 'alert-triangle',
    title: 'Try Again',
    message: 'Sorry, something went wrong. Please refresh the page to try again.',
  },
  CONTENT_DISCOVERY_NO_RESULT: {
    value: 'CONTENT_DISCOVERY_NO_FILTER',
    variant: 'sectionEmpty',
    title: 'No Content Matches Your Filters',
    message: 'Adjust your filters to view more content',
  },
  CONTENT_DISCOVERY_NO_RESULT_NLS: {
    value: 'CONTENT_DISCOVERY_NO_FILTER_AI',
    variant: 'sectionEmpty',
    icon: 'vision-ai',
    title: 'No Content Matches Your Filters',
    message: 'Adjust your prompt for different filters',
  },
  CONTENT_DISCOVERY_ERROR: {
    value: 'CONTENT_DISCOVERY_ERROR',
    variant: 'sectionEmpty',
    icon: 'alert-triangle',
    title: 'Something Went Wrong',
    message: 'We have an unexpected error, please retry',
  },
  IGCM_BUSINESS_VERIFICATION_REQUIRED: {
    value: 'IGCM_BUSINESS_VERIFICATION_REQUIRED',
    variant: 'pageError',
    icon: 'info-circle',
    title: 'Enroll in Creator Marketplace to Continue',
    message:
      'You’ve successfully connected Instagram and granted the required permissions. To continue accessing creator insights and branded collaboration features, enroll your business in the Creator Marketplace through Meta Business Suite.',
  },
  NOT_IN_TTCM: {
    value: 'NOT_IN_TTCM',
    variant: 'pageError',
    icon: 'alert-triangle',
    title: 'No Posts Found',
    message:
      'Due to TikTok’s data privacy rules, posts within a creator profile can only be displayed after they have accepted a connection request or enrolled in TikTok One Marketplace.',
  },
  PRIVATE_INSTAGRAM_ACCOUNT: {
    value: 'PRIVATE_INSTAGRAM_ACCOUNT',
    variant: 'pageError',
    icon: 'alert-triangle',
    title: 'Cannot Display Posts',
    message: 'This personal account is private and their feed posts cannot be imported.',
  },
  NO_CREATOR_EMAIL_ACCOUNT_CONNECTED: {
    value: 'NO_CREATOR_EMAIL_ACCOUNT_CONNECTED',
    icon: 'mail-unread',
    title: "Missing Creator's Email Address",
    message:
      'We don’t have this creator’s email address yet. You can add it in the Overview tab, or copy the Creator Details link and send it to them to complete their info.',
  },
  NO_EMAIL_ACCOUNT_CONNECTED: {
    value: 'NO_EMAIL_ACCOUNT_CONNECTED',
    icon: 'mail-unread',
    title: 'Connect Your Email',
    message: 'To message with this creator, connect your email to Dash Social.',
  },
  NO_CREATOR_THREADS: {
    variant: 'pageEmpty',
    icon: 'message',
    title: 'No Messages Yet',
    message:
      'You haven’t exchanged any messages with this creator. Start the conversation by sending them an email.',
  },
  EMAIL_API_ERROR: {
    value: 'EMAIL_API_ERROR',
    variant: 'sectionError',
    icon: 'alert-triangle',
    title: 'Try Again',
    message: 'There was a problem in retrieving emails. Please refresh the page in a few moments.',
  },
  NO_SIMILAR_TIKTOK_CREATORS: {
    value: 'NO_SIMILAR_TIKTOK_CREATORS',
    variant: 'pageError',
    icon: 'alert-triangle',
    title: 'No Similar Creators Found',
    center: true,
  },
  NO_SIMILAR_INSTAGRAM_CREATORS: {
    value: 'NO_SIMILAR_INSTAGRAM_CREATORS',
    variant: 'pageError',
    icon: 'alert-triangle',
    title: 'No Similar Creators Found',
    message: "There aren't enough posts published by this creator to find a similar creator.",
    center: true,
  },
  NO_CREATOR_CAMPAIGNS: {
    value: 'NO_CREATOR_CAMPAIGNS',
    variant: 'pageError',
    icon: 'message-alert',
    title: 'No Campaigns Yet',
    message:
      'This creator hasn’t been involved in any campaigns. Once they participate in a campaign, it will appear here for you to track and manage.',
  },
  NO_FILTERED_CREATOR_CAMPAIGNS: {
    value: 'NO_FILTERED_CREATOR_CAMPAIGNS',
    variant: 'pageEmpty',
    icon: 'search',
    title: 'No Campaigns Match Your Filters',
    message: 'Adjust your filters to view more campaigns',
  },
  ERROR_LOADING_CREATOR_CAMPAIGNS: {
    value: 'ERROR_LOADING_CREATOR_CAMPAIGNS',
    variant: 'pageError',
    icon: 'alert-triangle',
    title: 'Something Went Wrong',
    message:
      'We couldn’t load the campaigns for this creator. Please refresh the page or try again later.',
  },
  NO_RECENT_POSTS_FT_YOU: {
    value: 'NO_RECENT_POSTS_FT_YOU',
    variant: 'pageError',
    icon: 'message-alert',
    title: 'Waiting for Your Creators to Post about You',
    message:
      "Once Creators added to Your Creators post about you, you'll see that content highlighted here.",
  },
  NO_FILTERED_CREATOR_CAMPAIGN_POSTS: {
    value: 'NO_CREATOR_CAMPAIGN_POSTS',
    variant: 'pageEmpty',
    icon: 'search',
    title: 'No Posts Match Your Filters',
    message: 'Adjust your filters to view more posts',
  },
  NO_VALID_PLATFORM_TTCM_CONNECTION: {
    value: 'NO_VALID_PLATFORM_TTCM_CONNECTION',
    variant: 'sectionError',
    icon: 'alert-triangle',
    title: 'Connect Your TikTok One Marketplace Account',
    message:
      "To view this creator's posts, header metrics, and similar creators, your brand's TTO account needs to be connected. Without this connection, some data and features won’t be accessible.",
    ctaButton: {
      label: 'Connect',
    },
  },
  NO_SHOPIFY_PRODUCTS: {
    value: 'NO_SHOPIFY_PRODUCTS',
    variant: 'pageEmpty',
    title: 'No Products Available',
    message:
      "You don't have any products eligible for gifting right now. To get started, please make sure your Shopify store has active products in stock. If you just connected your Shopify account, your products may still be importing and will be available momentarily.",
  },
  NO_FILTERED_SHOPIFY_PRODUCTS: {
    value: 'NO_FILTERED_SHOPIFY_PRODUCTS',
    variant: 'pageEmpty',
    icon: 'search',
    title: 'No Products Match Your Filters',
    message: 'Adjust your filters to view more products',
  },
  NO_SHOPIFY_PRODUCTS_SELECTED: {
    value: 'NO_SHOPIFY_PRODUCTS_SELECTED',
    variant: 'sectionError',
    icon: 'alert-triangle',
    title: 'No Products Selected',
    message:
      "You don't have any products selected. Please select products from your Shopify store.",
  },
  NO_SHOPIFY_ACCOUNT_CONNECTED: {
    value: 'NO_SHOPIFY_ACCOUNT_CONNECTED',
    variant: 'pageEmpty',
    icon: 'shopify',
    title: 'Connect Your Shopify Store',
    message:
      'To send products to creators, you’ll need to connect your Shopify account. This lets you manage and track product gifting directly from your campaign.',
  },
  NO_CREATOR_PRODUCT_ORDERS: {
    value: 'NO_CREATOR_PRODUCT_ORDERS',
    variant: 'pageEmpty',
    icon: 'shopping-bag',
    title: 'Send Your Products',
    message:
      'You haven’t sent any products to this creator yet. Once you ship a product, it will appear here for easy tracking.',
  },
  NO_INFLUENTIAL_FANS: {
    value: 'NO_INFLUENTIAL_FANS',
    variant: 'pageEmpty',
    icon: 'user-avatar',
    title: 'No Influential Fans Yet',
    message:
      'When creators tag your brand in their posts, they’ll appear here, starting with the most influential ones.',
  },
  TIKTOK_ONE_CONNECTION_INVALID: {
    value: 'TIKTOK_ONE_CONNECTION_INVALID',
    variant: 'pageError',
    icon: 'alert-triangle',
    title: 'TikTok Connection is Invalid',
    message:
      'We couldn’t load your TikTok data because your connection is no longer valid. Please reconnect to see your Influential Fans.',
    btnText: 'Reconnect TikTok',
  },
  TT_ACCOUNT_PERFORMANCE_EMPTY: {
    value: 'TT_ACCOUNT_PERFORMANCE_EMPTY',
    variant: 'sectionEmpty',
    icon: 'user-avatar',
    title: 'Connect with the Creator',
    message: "This creator isn't connected. Send an invite to unlock the full profile data.",
    ctaButton: { label: 'Invite to Connect' },
  },
  TOP_COMPETITORS_EMPTY: {
    icon: 'settings',
    title: 'Set Up Your Competitor List',
    message:
      'Add competitor accounts in Settings to track which creators are posting about them and how your brand stacks up',
    variant: 'sectionEmpty',
    ctaButton: { label: 'Add Competitors', severity: 'tertiary' },
  },
  COMPETITIVE_MENTIONS_NO_POSTS: {
    title: 'Competitor Mentions Will Appear Here',
    message: 'We’ll display relevant posts as they appear',
    variant: 'sectionEmpty',
  },
  COMPETITIVE_MENTIONS_FILTERS: {
    title: 'Adjust Filters to See Results',
    message: 'Adjust your filters to view competitor mentions and their posts',
    variant: 'pageEmpty',
    ctaButton: { label: 'Clear Filters', severity: 'tertiary' },
  },
  COMPETITIVE_MENTIONS_NO_MENTIONS: {
    title: 'Competitor Mentions Will Appear Here',
    message:
      'This creator hasn’t mentioned any of your competitors yet. We’ll display relevant posts as they appear.',
    icon: 'at-sign',
    variant: 'pageEmpty',
  },
  COMPETITIVE_MENTIONS_NO_SUGGESTIONS: {
    title: 'All Suggested Competitors Added',
    message:
      'You’ve added every suggested competitor. New ones will appear when creators mention them.',
    variant: 'sectionEmpty',
  },
  NO_AFFILIATE_LINKS: {
    title: 'Create Your First Affiliate Link',
    message: 'Add an affiliate link to start tracking this creator’s performance and engagement.',
    icon: 'link',
    variant: 'pageEmpty',
    ctaButton: { label: 'Add Affiliate Link' },
  },
  NO_CREATOR_CAMPAIGNS_FOR_AFFILIATE_LINKS: {
    title: 'Add Creator to Campaign',
    message:
      'This creator is not associated with any campaigns yet. Add them to a campaign to enable affiliate links.',
    icon: 'campaign',
    variant: 'pageEmpty',
  },
  NO_BRANDED_CONTENT: {
    title: 'Branded Content Will Display Here',
    message: "This creator hasn't published any branded content.",
    variant: 'sectionEmpty',
    icon: 'info-circle',
  },
  BRANDED_CONTENT_GENERIC: {
    title: 'Branded Content Will Display Here',
    message:
      'We’re having trouble loading this creator’s branded content right now. Please try again later.',
    variant: 'sectionEmpty',
    icon: 'info-circle',
  },
  IGCM_RATE_LIMIT: {
    title: "You've reached the current limit",
    message: "Branded content feed isn't currently available. Please try again within the hour. ",
    variant: 'sectionEmpty',
    icon: 'info-circle',
  },
  IGCM_MISSING_PLATFORM_CONNECTION_AND_SCOPES: {
    value: 'NO_SIMILAR_INSTAGRAM_CREATORS_MISSING_SCOPES',
    variant: 'pageEmpty',
    title: 'Reconnect Instagram to Continue',
    message: igcmReconnectionBannerMessage,
  },
  DISCOVERY_RATE_LIMIT: {
    variant: 'pageError',
    icon: 'alert-triangle',
    title: 'Some Filter Limits Have Been Reached',
  },
  IGCM_PARTNERSHIP_DMS_INELIGIBLE: {
    variant: 'pageEmpty',
    icon: 'message-alert',
    title: 'Partnership DMs Unavailable',
    message:
      "This creator has more than 250K followers and isn't eligible to receive partnership messages on Instagram",
  },
  IGCM_PARTNERSHIP_DMS_OPTED_OUT: {
    variant: 'pageEmpty',
    icon: 'instagram-color',
    title: "This Creator Isn't Accepting Partnership DMs",
    message: '@handle has turned off partnership messages from brands on Instagram',
  },
  IGCM_PARTNERSHIP_DMS_RECONNECT: {
    variant: 'pageError',
    icon: 'instagram-color',
    title: 'Reconnect Instagram to Send DMs',
    message:
      'To send Partnership DMs, your Instagram account needs to be reconnected with Creator Marketplace Messaging & Mailbox permissions.',
  },
  IGCM_PARTNERSHIP_DMS_RATE_LIMIT: {
    variant: 'pageError',
    icon: 'alert-triangle',
    title: "You've reached the current limit",
    message: "Partnership DMs aren't currently available. Please try again within the hour.",
  },
};

export const locations = [
  {
    type: 'item',
    label: 'Argentina',
    value: 'Argentina',
    code: 'AR',
  },
  {
    type: 'item',
    label: 'Australia',
    value: 'Australia',
    code: 'AU',
  },
  {
    type: 'item',
    label: 'Austria',
    value: 'Austria',
    code: 'AT',
  },
  {
    type: 'item',
    label: 'Belgium',
    value: 'Belgium',
    code: 'BE',
  },
  {
    type: 'item',
    label: 'Brazil',
    value: 'Brazil',
    code: 'BR',
  },
  {
    type: 'item',
    label: 'Canada',
    value: 'Canada',
    code: 'CA',
  },
  {
    type: 'item',
    label: 'Chile',
    value: 'Chile',
    code: 'CL',
  },
  {
    type: 'item',
    label: 'China',
    value: 'China',
    code: 'CN',
  },
  {
    type: 'item',
    label: 'Colombia',
    value: 'Colombia',
    code: 'CO',
  },
  {
    type: 'item',
    label: 'Costa Rica',
    value: 'Costa Rica',
    code: 'CR',
  },
  {
    type: 'item',
    label: 'Croatia',
    value: 'Croatia',
    code: 'HR',
  },
  {
    type: 'item',
    label: 'Czech Republic',
    value: 'Czech Republic',
    code: 'CZ',
  },
  {
    type: 'item',
    label: 'Denmark',
    value: 'Denmark',
    code: 'DK',
  },
  {
    type: 'item',
    label: 'Dominican Republic',
    value: 'Dominican Republic',
    code: 'DO',
  },
  {
    type: 'item',
    label: 'Finland',
    value: 'Finland',
    code: 'FI',
  },
  {
    type: 'item',
    label: 'France',
    value: 'France',
    code: 'FR',
  },
  {
    type: 'item',
    label: 'Germany',
    value: 'Germany',
    code: 'DE',
  },
  {
    type: 'item',
    label: 'Greece',
    value: 'Greece',
    code: 'GR',
  },
  {
    type: 'item',
    label: 'Hungary',
    value: 'Hungary',
    code: 'HU',
  },
  {
    type: 'item',
    label: 'India',
    value: 'India',
    code: 'IN',
  },
  {
    type: 'item',
    label: 'Indonesia',
    value: 'Indonesia',
    code: 'ID',
  },
  {
    type: 'item',
    label: 'Ireland',
    value: 'Ireland',
    code: 'IE',
  },
  {
    type: 'item',
    label: 'Italy',
    value: 'Italy',
    code: 'IT',
  },
  {
    type: 'item',
    label: 'Israel',
    value: 'Israel',
    code: 'IL',
  },
  {
    type: 'item',
    label: 'Japan',
    value: 'Japan',
    code: 'JP',
  },
  {
    type: 'item',
    label: 'Kuwait',
    value: 'Kuwait',
    code: 'KW',
  },
  {
    type: 'item',
    label: 'Lebanon',
    value: 'Lebanon',
    code: 'LB',
  },
  {
    type: 'item',
    label: 'Malaysia',
    value: 'Malaysia',
    code: 'MY',
  },
  {
    type: 'item',
    label: 'Mexico',
    value: 'Mexico',
    code: 'MX',
  },
  {
    type: 'item',
    label: 'Morocco',
    value: 'Morocco',
    code: 'MA',
  },
  {
    type: 'item',
    label: 'Netherlands',
    value: 'Netherlands',
    code: 'NL',
  },
  {
    type: 'item',
    label: 'New Zealand',
    value: 'New Zealand',
    code: 'NZ',
  },
  {
    type: 'item',
    label: 'Nigeria',
    value: 'Nigeria',
    code: 'NG',
  },
  {
    type: 'item',
    label: 'Norway',
    value: 'Norway',
    code: 'NO',
  },
  {
    type: 'item',
    label: 'Pakistan',
    value: 'Pakistan',
    code: 'PK',
  },
  {
    type: 'item',
    label: 'Philippines',
    value: 'Philippines',
    code: 'PH',
  },
  {
    type: 'item',
    label: 'Poland',
    value: 'Poland',
    code: 'PL',
  },
  {
    type: 'item',
    label: 'Portugal',
    value: 'Portugal',
    code: 'PT',
  },
  {
    type: 'item',
    label: 'Romania',
    value: 'Romania',
    code: 'RO',
  },
  {
    type: 'item',
    label: 'Russia',
    value: 'Russia',
    code: 'RU',
  },
  {
    type: 'item',
    label: 'Serbia',
    value: 'Serbia',
    code: 'RS',
  },
  {
    type: 'item',
    label: 'Saudi Arabia',
    value: 'Saudi Arabia',
    code: 'SA',
  },
  {
    type: 'item',
    label: 'Singapore',
    value: 'Singapore',
    code: 'SG',
  },
  {
    type: 'item',
    label: 'South Africa',
    value: 'South Africa',
    code: 'ZA',
  },
  {
    type: 'item',
    label: 'South Korea',
    value: 'South Korea',
    code: 'KR',
  },
  {
    type: 'item',
    label: 'Spain',
    value: 'Spain',
    code: 'ES',
  },
  {
    type: 'item',
    label: 'Sweden',
    value: 'Sweden',
    code: 'SE',
  },
  {
    type: 'item',
    label: 'Switzerland',
    value: 'Switzerland',
    code: 'CH',
  },
  {
    type: 'item',
    label: 'Taiwan',
    value: 'Taiwan',
    code: 'TW',
  },
  {
    type: 'item',
    label: 'Thailand',
    value: 'Thailand',
    code: 'TH',
  },
  {
    type: 'item',
    label: 'Turkey',
    value: 'Turkey',
    code: 'TR',
  },
  {
    type: 'item',
    label: 'Ukraine',
    value: 'Ukraine',
    code: 'UA',
  },
  {
    type: 'item',
    label: 'United Arab Emirates',
    value: 'United Arab Emirates',
    code: 'AE',
  },
  {
    type: 'item',
    label: 'United Kingdom',
    value: 'United Kingdom',
    code: 'GB',
  },
  {
    type: 'item',
    label: 'United States',
    value: 'United States',
    code: 'US',
  },
  {
    type: 'item',
    label: 'Venezuela',
    value: 'Venezuela',
    code: 'VE',
  },
  {
    type: 'item',
    label: 'Vietnam',
    value: 'Vietnam',
    code: 'VN',
  },
];

export const months = [
  { type: 'item', label: 'January', value: 1 },
  { type: 'item', label: 'February', value: 2 },
  { type: 'item', label: 'March', value: 3 },
  { type: 'item', label: 'April', value: 4 },
  { type: 'item', label: 'May', value: 5 },
  { type: 'item', label: 'June', value: 6 },
  { type: 'item', label: 'July', value: 7 },
  { type: 'item', label: 'August', value: 8 },
  { type: 'item', label: 'September', value: 9 },
  { type: 'item', label: 'October', value: 10 },
  { type: 'item', label: 'November', value: 11 },
  { type: 'item', label: 'December', value: 12 },
];

export const creatorOverviewPageTooltips = {
  MOST_RECENT_POSTS: 'The most recent posts @mentioning you by any creator added to Your Creators',
  YOUR_CREATORS: 'The creators you’ve most recently added to Your Creators',
  CAMPAIGNS: 'The Campaigns you’ve created most recently',
  SIMILAR_TIKTOK_CREATORS:
    'TikTok creators most similar to the last creator you’ve added to Your Creators. Similarity is based on TikTok’s recommendation model.',
  SIMILAR_INSTAGRAM_CREATORS:
    'Instagram creators most similar to the last creator you’ve added to Your Creators. Similarity is based on a custom recommendation model factoring in location, interests and similar visuals.',
  INFLUENTIAL_FANS: 'The top creators that have @mentioned or tagged you in their posts',
};

export const competitiveMentionsTooltips = {
  TOP_COMPETITOR_MENTIONS:
    "The competitor brands you've added that are most frequently mentioned by this creator",
  SUGGESTED_COMPETITORS:
    "The competitor brands we've automatically detected that are mentioned by this creator",
};

export const youtubePerformanceMetricTooltips = {
  FOLLOWERS: 'The number of subscribers this creator has on YouTube',
  POST_COUNT: 'The total number of posts this creator published',
  AVG_WEEKLY_POSTS:
    'The average number of posts this creator publishes per week, measured from Sunday to Saturday. This is a 12-week rolling average of recent posts.',
  AVG_ENGAGEMENTS:
    "This creator's average engagements per post, based on a 12-week rolling average of recent posts \n\nEngagements = Likes + Comments",
  AVG_ENGAGEMENT_RATE:
    "This creator's average engagement rate, based on a 12-week rolling average of recent posts \n\nEngagement Rate = (Likes + Comments) / Views",
  AVG_EMV:
    'The average earned media value of this creator, based on a 12-week rolling average of recent posts.\n\n' +
    'EMV is calculated by factoring in the channel, content type, engagement and current market values for comparable posts.',
  VIEWS: "This creator's total video views, based on a 12-week rolling average of recent posts",
  AVG_VIEWS:
    "This creator's average video views per post, based on a 12-week rolling average of recent posts",
  AVG_LIKES:
    "This creator's average likes per post, based on a 12-week rolling average of recent posts",
  LIKES: "This creator's total likes, based on a 12-week rolling average of recent posts",
  COMMENTS: "This creator's total comments, based on a 12-week rolling average of recent posts",
  AVG_COMMENTS:
    "This creator's average comments per post, based on a 12-week rolling average of recent posts",
};

export const creatorPerformanceMetricTooltips = {
  FOLLOWERS: 'The number of followers this creator has on Instagram',
  AVG_WEEKLY_POSTS:
    'The average number of posts this creator publishes per week, measured from Sunday to Saturday. This is a 12-week rolling average of recent posts.',
  AVG_ENGAGEMENT_RATE: `This creator's average engagement rate, based on a 12-week rolling average of recent posts\n\nEngagement Rate = (Likes + Comments) / Followers`,
  AVG_ENGAGEMENTS: `This creator's average engagements per post, based on a 12-week rolling average of recent posts\n\nEngagements = Likes + Comments + (Saves + Shares + Reposts if connected)`,
  AVG_EFFECTIVENESS: `This creator's average Effectiveness per post, based on a 12-week rolling average of recent posts. For accounts that are not connected, reach is estimated.
\n\nEffectiveness (Posts / Reels) = Likes + Comments (+ Saves + Shares + Reposts if connected) / Reach`,
  TOTAL_EMV:
    'The average earned media value of this creator, based on a 12-week rolling average of recent posts.\n\n' +
    'EMV is calculated by factoring in the channel, content type, engagement and current market values for comparable posts.',
  AVG_ESTIMATED_REACH: `The average estimated reach per post. This is a 12 week rolling average across this creator's most recent posts.`,
  AVG_REACH: `The average reach per post. This is a 12-week rolling average across this creator's most recent posts.`,
  AVG_EMV:
    'The average earned media value of this creator, based on a 12-week rolling average of recent posts.\n\n' +
    'EMV is calculated by factoring in the channel, content type, engagement and current market values for comparable posts.',
  ENGAGED_ACCOUNTS:
    'The number of unique accounts that interacted with the creator’s content (via like, save, comment, share, or reply) in the past 30 days',
  REACH:
    'The number of unique accounts that have seen the creator’s posts at least once in the past 30 days',
  INTERACTION_RATE:
    'The percentage of views that lead to likes, comments, shares, or saves on this creator’s recent Reels in the past 90 days.\n\n' +
    'Reel Interaction Rate =  Engaged Views / Total Initial Views.',
  HOOK_RATE:
    'The percentage of people who watched the first 3 seconds of the creator’s Reel in the past 90 days.\n\n' +
    'Reel Hook Rate = (3-second views / initial plays) × 100.',
};

export const tikTokAccountMetricTooltips = {
  FOLLOWERS: 'The number of followers this creator has on TikTok',
  AVG_WEEKLY_POSTS:
    'The average number of posts this creator publishes per week. This is a 12 week rolling average.',
  AVG_ENGAGEMENT_RATE: `This creator's average engagement rate, based on their 30 most recent posts\n\nEngagement Rate = (Likes + Shares + Comments) / Video Views`,
  AVG_ENGAGEMENTS: `This creator's average engagements per post, based on their 30 most recent posts\n\nEngagements = Likes + Shares + Comments`,
  AVG_VIDEO_VIEWS: `This creator's average video views per post, based on their 30 most recent posts`,
  AVG_COMMENTS: `This creator's average comments per post, based on their 30 most recent posts`,
};

export const tikTokAccountPerformanceMetricTooltips = {
  MEDIAN_VIEWS: "The middle value in the range of views across the creator's recent 30 videos.",
  AVG_ENGAGEMENTS: `This creator's average engagement rate, based on their 30 most recent posts \n\nEngagements = Likes + Shares + Comments`,
  AVG_VIDEO_VIEWS: `This creator's average video views per post, based on their 30 most recent posts`,
  AVG_WEEKLY_POSTS:
    'The average number of posts this creator publishes per week. This is a 12 week rolling average.',
  AVG_ENGAGEMENT_RATE:
    "This creator's average engagement rate, based on their 30 most recent posts \n\nEngagement Rate = (Likes + Shares + Comments) / Video Views",
  TOTAL_EMV:
    'The total earned media value of this creator’s 30 most recent posts.\n\n' +
    'EMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
  TOTAL_LIKES: 'The total number of likes this creator has received across all videos',
  VIDEO_COUNT: 'The total number of videos this creator has published',
};

export const tikTokCreatorMetricTooltips = {
  FOLLOWERS: 'The total number of followers this creator has on TikTok',
  AVG_ENGAGEMENT_RATE: `This creator's average engagement rate, based on their 30 most recent posts \n\nEngagement Rate = (Likes + Shares + Comments) / Video Views`,
  AVG_ENGAGEMENTS: `This creator's average engagements per post, based on their 30 most recent posts \n\nEngagements = Likes + Shares + Comments`,
  AVG_VIDEO_VIEWS: `This creator's average video views per post, based on their 30 most recent posts`,
  AVG_WEEKLY_POSTS:
    'The average number of posts this creator publishes per week. This is a 12 week rolling average.',
  AVG_EMV:
    'The average earned media value of this creator’s 30 most recent posts.\n\n' +
    'EMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
};

export const tiktokPostInsightsMetricTooltips = {
  AVG_LIKES:
    'The average likes per post that tagged or mentioned your brand during the reporting period',
  AVG_COMMENTS:
    'The average comments per post that tagged or mentioned your brand during the reporting period',
  AVG_SHARES:
    'The average shares per post that tagged or mentioned your brand during the reporting period',
  AVG_VIDEO_VIEWS:
    'The average video views per post that tagged or mentioned your brand during the reporting period',
  TOTAL_EMV:
    'The total earned media value of posts during the reporting period.\n\n' +
    'EMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
  AVG_ENGAGEMENT:
    'The average number of engagements per post that tagged or mentioned your brand during the reporting period \n\nEngagements = Likes + Shares + Comments',
  AVG_ENGAGEMENT_RATE:
    'The average engagement rate per post that tagged or mentioned your brand during the reporting period \n\nEngagement Rate = (Likes + Shares + Comments) / Video Views',
};

export const tiktokDiscoveryFilterTooltips = {
  LOCATION: 'The country where a creator resides. Only one location may be selected at a time.',
  LANGUAGE: 'The language of the creator',
  KEYWORDS: 'Search for creators using keywords in their bio or post captions',
  FOLLOWERS: 'The number of followers a creator has',
  ENGAGEMENT_RATE: 'The average engagement rate of a creator’s last 30 videos',
  AVERAGE_VIEWS_PER_VIDEO:
    'Average count of views per video, based on the creator’s last 30 videos',
  MOBILE_OS: 'The device the creator uses',
  CAMPAIGN_COUNT:
    'The number of campaigns completed by the creator within the TikTok One Marketplace',
  CREATOR_RESPONSIVENESS:
    'Creators who have earned a ‘Responsive’ badge in the TikTok One Marketplace. These creators typically respond to requests within 48 hours.',
  MEDIAN_VIEWS: "The middle value in the range of views across the creator's recent 30 videos",
  TIKTOK_CREATOR_AUDIENCE_FILTERS: {
    LOCATION:
      'The region that the majority of the creators’ followers are based in. Only one location may be selected at a time.',
    GENDER: 'The gender that the majority of the creators’ followers fall into',
    GENDER_RATIO: 'The gender that the majority of the creators’ followers fall into',
    AGE: "The age range that the majority of the creators' followers fall into",
  },
  CREATOR_RATES: 'The creator’s typical charge for a single collaboration or deliverable',
};

export const instagramPostInsightsMetricTooltips = {
  AVG_ENGAGEMENT_RATE:
    'The average engagement rate per post that tagged or mentioned you during the reporting period \n\nEngagement Rate = (Likes + Comments) / Followers',
  AVG_ENGAGEMENT_PER_POST:
    'The average number of engagements per post that tagged or mentioned you during the reporting period\nLikes + Comments (+ Saves + Shares + Reposts if connected)',
  AVG_VIEWS:
    'The average number of views per Reel this creator collaborated with your brand on during the reporting period',
  TOTAL_VIEWS:
    'The total number of views across Reels this creator collaborated with your brand on during the reporting period',
  TOTAL_FOLLOWERS_GAINED:
    'The estimated number of followers this creator has gained for you by tagging or mentioning your account in their posts',
  AVG_EFFECTIVENESS: `This average Effectiveness per post that tagged or mentioned you. For accounts that are not connected, reach is estimated.
    \n\nEffectiveness (Posts / Reels) = Likes + Comments (+ Saves + Shares + Reposts if connected) / Reach`,
  TOTAL_EMV:
    'The total earned media value of this creator’s posts that tagged or mentioned your brand.\n\n' +
    'EMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
};

export const instagramCollaborationMetricTooltips = {
  AVG_ENGAGEMENT_RATE:
    'The average engagement rate per post this creator collaborated with your brand on during the reporting period \n\nEngagement Rate = (Likes + Comments) / Followers',
  AVG_ENGAGEMENTS:
    'The average number of engagements per post this creator collaborated with your brand on during the reporting period\n\nEngagements = Likes + Comments (+ Saves + Shares + Reposts if connected)',
  AVG_VIEWS:
    'The average number of views per Reel this creator collaborated with your brand on during the reporting period',
  TOTAL_VIEWS:
    'The total number of views across Reels this creator collaborated with your brand on during the reporting period',
  AVG_EFFECTIVENESS: `The average effectiveness per post this creator collaborated with your brand on during the reporting period. For accounts that are not connected, reach is estimated.
    \n\nEffectiveness (Posts / Reels) = Likes + Comments (+ Saves + Shares + Reposts if connected) / Reach`,
  TOTAL_EMV:
    'The total earned media value of posts this creator collaborated with your brand on during the reporting period.\n\n' +
    'EMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
};

export const instagramStoryInsightsMetricTooltips = {
  AVG_REACH:
    'The average unique accounts this creator reached per story that tagged or mentioned you',
  AVG_IMPRESSIONS:
    "The average number of times this creator's stories that tagged or mentioned you were displayed ",
  AVG_EXIT_RATE:
    "The average exit rate of this creator's stories that tagged or mentioned you \n\nExit Rate = Exits / Impressions",
  AVG_COMPLETION_RATE:
    "The average completion rate of this creator's stories that tagged or mentioned you \n\nCompletion Rate = (Impressions - Exits) / Impressions",
  AVG_EXIT_RATE_VIEWS:
    "The average exit rate of this creator's stories that tagged or mentioned you \n\nExit Rate = Exits / Views",
  AVG_COMPLETION_RATE_VIEWS:
    "The average completion rate of this creator's stories that tagged or mentioned you \n\nCompletion Rate = (Views - Exits) / Views",
  AVG_VIDEO_VIEWS: "The number of times this creator's posts were viewed for 1 ms or more",
};

export const youtubePostInsightsMetricTooltips = {
  TOTAL_EMV:
    'The total earned media value of this creator’s posts that tagged or mentioned you during the reporting period.\n\n' +
    'EMV is calculated by factoring in the channel, audience size, content type, engagement and current market values for comparable posts.',
  TOTAL_VIEWS:
    'The total video views per post that tagged or mentioned you during the reporting period',
  AVG_VIEWS:
    'The average video views per post that tagged or mentioned you during the reporting period',
  AVG_ENGAGEMENTS:
    'The average number of engagements per post that tagged or mentioned you during the reporting period \n\nEngagements = Likes + Comments',
  AVG_ENGAGEMENT_RATE:
    'The average engagement rate per post that tagged or mentioned you during the reporting period \n\nEngagement Rate = (Likes + Comments) / Views',
  TOTAL_LIKES: 'The total likes per post that tagged or mentioned you during the reporting period',
  AVG_LIKES: 'The average likes per post that tagged or mentioned you during the reporting period',
  TOTAL_COMMENTS:
    'The total comments per post that tagged or mentioned you during the reporting period',
  AVG_COMMENTS:
    'The average comments per post that tagged or mentioned you during the reporting period',
};

const instagramPostMetrics = ['likeCount', 'commentsCount', 'emv'];
const instagramPostMetricLabels = {
  likeCount: 'Likes',
  commentsCount: 'Comments',
  impressions: 'Impressions',
  views: 'Views',
  emv: 'EMV',
};

const instagramBrandedContentMetrics = ['likes', 'comments', 'shares', 'views'];
const instagramBrandedContentMetricLabels = {
  likes: 'Likes',
  comments: 'Comments',
  shares: 'Shares',
  views: 'Views',
};

const youtubeRelationshipPostMetrics = ['emv', 'views', 'likes', 'comments'];
const youtubeRelationshipPostMetricLabels = {
  emv: 'EMV',
  views: 'Views',
  likes: 'Likes',
  comments: 'Comments',

  // The following are only included when the sort value is the same label.
  engagementRate: 'Engagement Rate',
  avgViewDuration: 'Avg. View Duration',
  avgViewPercentage: 'Avg. % Viewed',
  dislikes: 'Dislikes',
  engagements: 'Engagements',
  estSecondsWatched: 'Est. Seconds Watched',
  estPremiumSecondsWatched: 'Est. Premium Seconds Watched',
  premiumViews: 'Premium Views',
  shares: 'Shares',
};

const instagramRelationshipPostMetrics = ['engagement', 'likeCount', 'commentsCount', 'emv'];
const instagramRelationshipPostMetricLabels = {
  engagement: 'Engagement Rate',
  likeCount: 'Likes',
  commentsCount: 'Comments',
  emv: 'EMV',
  // The following are only included when the sort value is the same label.
  followersGained: 'Followers Gained',
  impressions: 'Impressions',
  views: 'Views',
  effectiveness: 'Effectiveness',
  reach: 'Reach',
  saved: 'Saves',
  totalEngagement: 'Total Engagement',
  videoViews: 'Views',
};

const instagramRelationshipPostMetricUpdateLabels = {
  engagement: 'Engagement Rate',
  likeCount: 'Likes',
  commentsCount: 'Comments',
  emv: 'EMV',
  // The following are only included when the sort value is the same label.
  followersGained: 'Followers Gained',
  impressions: 'Impressions',
  effectivenessEngagements: 'Effectiveness',
  reach: 'Reach',
  saved: 'Saves',
  totalEngagement: 'Total Engagement',
  views: 'Views',
};

const instagramStoryRelationshipPostMetrics = ['reach', 'views', 'completionRate', 'emv'];

const instagramStoryRelationshipPostMetricLabels = {
  completionRate: 'Completion Rate',
  impressions: 'Impressions',
  views: 'Views',
  reach: 'Reach',
  emv: 'EMV',
  // The following are only included when the sort value is the same label.
  exitRate: 'Exit Rate',
  exits: 'Exits',
};

const instagramStoryRelationshipPostMetricsUpdate = [
  'reach',
  'viewsV2',
  'completionRateViews',
  'emvViews',
];
const instagramStoryRelationshipPostMetricUpdateLabels = {
  completionRateViews: 'Completion Rate',
  viewsV2: 'Views',
  reach: 'Reach',
  emvViews: 'EMV',
  // The following are only included when the sort value is the same label.
  exitRateViews: 'Exit Rate',
  exits: 'Exits',
};

const tikTokAccountFeedPostMetrics = ['views', 'likes', 'comments', 'shares'];
const tikTokAccountFeedPostMetricLabels = {
  views: 'Video Views',
  likes: 'Likes',
  comments: 'Comments',
  shares: 'Shares',
};

const tikTokCreatorFeedPostMetrics = ['views', 'likes', 'comments', 'emv'];
const tikTokCreatorFeedPostMetricLabels = {
  views: 'Video Views',
  likes: 'Likes',
  comments: 'Comments',
  emv: 'EMV',
  totalEngagements: 'Engagements',
  engagementRate: 'Engagement Rate',
  reach: 'Reach',
  shares: 'Shares',
};

const tikTokCreatorRIQMetricsConnected = ['views', 'totalEngagements', 'engagementRate', 'emv'];
const tikTokCreatorRIQConnectedMetricLabels = {
  views: 'Video Views',
  totalEngagements: 'Engagements',
  engagementRate: 'Engagement Rate',
  emv: 'EMV',
  // The following are only included when the sort value is the same label.
  shares: 'Shares',
  likes: 'Likes',
  comments: 'Comments',
};

const tikTokCreatorFeedMetricsConnected = ['views', 'engagementCount', 'engagementRate', 'emv'];
const tikTokCreatorFeedConnectedMetricLabels = {
  views: 'Video Views',
  engagementCount: 'Engagements',
  engagementRate: 'Engagement Rate',
  emv: 'EMV',
};

const tikTokCreatorRIQMetricsNotConnected = ['views', 'likes', 'comments', 'emv'];
const tikTokCreatorRIQNotConnectedMetricLabels = {
  views: 'Video Views',
  likes: 'Likes',
  comments: 'Comments',
  emv: 'EMV',
  // The following are only included when the sort value is the same label.
  shares: 'Shares',
  totalEngagements: 'Engagements',
  engagementRate: 'Engagement Rate',
};

const youtubePostMetrics = ['views', 'likes', 'comments', 'emv'];
const youtubePostMetricLabels = {
  views: 'Views',
  likes: 'Likes',
  comments: 'Comments',
  emv: 'EMV',
  // The following are only included when the sort value is the same label.
  engagementRate: 'Engagement Rate',
  engagements: 'Engagements',
};

// TikTok metrics for all post types
export const tikTokPostMetrics = ['views', 'engagementCount', 'engagementRate', 'emv'];
export const tikTokPostMetricLabels = {
  views: 'Video Views',
  engagementCount: 'Engagements',
  engagementRate: 'Engagement Rate',
  emv: 'EMV',
  // The following are only included when the sort value is the same label.
  likes: 'Likes',
  comments: 'Comments',
  shares: 'Shares',
};

export const discoveryType = {
  CREATOR: 'CREATOR',
  CONTENT: 'CONTENT',
};

export const creatorSource = {
  INSTAGRAM: 'INSTAGRAM',
  TIKTOK: 'TIKTOK',
  YOUTUBE: 'YOUTUBE',
};

export const creatorSources = [
  creatorSource.INSTAGRAM,
  creatorSource.TIKTOK,
  creatorSource.YOUTUBE,
];

export const instagramRelationshipPostListTypeOptions = {
  FEED: { value: 'IG_FEED', label: 'Entire Feed' },
  BRANDED_CONTENT: {
    value: 'IG_BRANDED_CONTENT',
    label: 'Branded Content',
  },
  RIQ: { value: 'IG_RIQ', label: 'Posts Featuring You' },
  COLLABORATIONS: { value: 'IG_COLLABORATIONS', label: 'Your Collaborations' },
};

export const tikTokConnectedRIQPostListTypeOptions = {
  FEED: { value: 'TT_CREATOR_FEED_CONNECTED', label: 'Entire Feed' },
  RIQ: { value: 'TT_CREATOR_RIQ_CONNECTED', label: 'Posts Featuring You' },
};

export const tikTokNotConnectedRIQPostListTypeOptions = {
  FEED: { value: 'TT_CREATOR_FEED', label: 'Entire Feed' },
  RIQ: { value: 'TT_CREATOR_RIQ_NOT_CONNECTED_OR_NOT_IN_TTCM', label: 'Posts Featuring You' },
};

export const tikTokNotConnectedNotInTtoPostListTypeOptions = {
  FEED: {
    value: 'TT_CREATOR_FEED',
    label: 'Entire Feed',
    disabled: true,
    tooltip: 'Please check TikTok One connection status',
  },
  RIQ: { value: 'TT_CREATOR_RIQ_NOT_CONNECTED_OR_NOT_IN_TTCM', label: 'Posts Featuring You' },
};

export const youtubePostListTypeOptions = {
  FEED: { value: 'YT_FEED', label: 'Entire Feed' },
  RIQ: { value: 'YT_RIQ', label: 'Posts Featuring You' },
};

export const youtubePostTypeOptions = {
  ALL: { value: 'ALL', label: 'All Video Types' },
  VIDEOS: { value: 'VIDEOS', label: 'Videos' },
  SHORTS: { value: 'SHORTS', label: 'Shorts' },
};

export const youtubePostTypeForInsights = {
  SHORT: { value: 'SHORTS' },
  VIDEO: { value: 'VIDEO' },
};

export const tikTokCreatorConnectedSortOptions = {
  COMMENTS: {
    value: 'COMMENTS',
    label: 'Comments',
    sortValue: 'TIKTOK_COMMENTS',
  },
  ENGAGEMENT_RATE: {
    value: 'ENGAGEMENT_RATE',
    label: 'Engagement Rate',
    sortValue: 'TIKTOK_ENGAGEMENT_RATE',
  },
  ENGAGEMENTS: {
    value: 'ENGAGEMENTS',
    label: 'Engagements',
    sortValue: 'TIKTOK_TOTAL_ENGAGEMENTS',
  },
  EMV: {
    value: 'EMV',
    label: 'EMV',
    sortValue: 'TIKTOK_EMV',
  },
  LIKES: {
    value: 'LIKES',
    label: 'Likes',
    sortValue: 'TIKTOK_LIKES',
  },
  RECENTLY_PUBLISHED: {
    value: 'RECENTLY_PUBLISHED',
    label: 'Recently Published',
    sortValue: 'DATE',
  },
  SHARES: {
    value: 'SHARES',
    label: 'Shares',
    sortValue: 'TIKTOK_SHARES',
  },
  VIDEO_VIEWS: {
    value: 'VIDEO_VIEWS',
    label: 'Video Views',
    sortValue: 'TIKTOK_VIEWS',
  },
};

export const tikTokCreatorNotConnectedSortOptions = {
  COMMENTS: {
    value: 'COMMENTS',
    label: 'Comments',
    sortValue: 'TIKTOK_COMMENTS',
  },
  EMV: {
    value: 'EMV',
    label: 'EMV',
    sortValue: 'TIKTOK_EMV',
  },
  LIKES: {
    value: 'LIKES',
    label: 'Likes',
    sortValue: 'TIKTOK_LIKES',
  },
  RECENTLY_PUBLISHED: {
    value: 'RECENTLY_PUBLISHED',
    label: 'Recently Published',
    sortValue: 'DATE',
  },
  SHARES: {
    value: 'SHARES',
    label: 'Shares',
    sortValue: 'TIKTOK_SHARES',
  },
  VIDEO_VIEWS: {
    value: 'VIDEO_VIEWS',
    label: 'Video Views',
    sortValue: 'TIKTOK_VIEWS',
  },
};

export const youtubePostSortOptions = {
  RECENTLY_PUBLISHED: {
    value: 'RECENTLY_PUBLISHED',
    label: 'Recently Published',
    sortValue: 'DATE',
  },
  ENGAGEMENT_RATE: {
    sortValue: 'YOUTUBE_ENGAGEMENT_RATE',
    label: 'Engagement Rate',
    value: 'ENGAGEMENT_RATE',
  },
  ENGAGEMENTS: { sortValue: 'YOUTUBE_ENGAGEMENTS', label: 'Engagements', value: 'ENGAGEMENTS' },
  LIKES: { sortValue: 'YOUTUBE_LIKES', label: 'Likes', value: 'LIKES' },
  COMMENTS: { sortValue: 'YOUTUBE_COMMENTS', label: 'Comments', value: 'COMMENTS' },
  VIEWS: { sortValue: 'YOUTUBE_VIEWS', label: 'Views', value: 'VIEWS' },
  EMV: { sortValue: 'YOUTUBE_EMV', label: 'EMV', value: 'EMV' },
};

export const instagramRelationshipPostSortOptions = {
  COMMENTS: { value: 'COMMENTS', label: 'Comments', sortValue: 'INSTAGRAM_TOTAL_COMMENTS' },
  EFFECTIVENESS: {
    value: 'EFFECTIVENESS',
    label: 'Effectiveness',
    sortValue: 'INSTAGRAM_EFFECTIVENESS',
  },
  EFFECTIVENESS_ENGAGEMENTS: {
    value: 'EFFECTIVENESS_ENGAGEMENTS',
    label: 'Effectiveness',
    sortValue: 'INSTAGRAM_EFFECTIVENESS_ENGAGEMENTS',
  },
  EMV: { value: 'EMV', label: 'EMV', sortValue: 'INSTAGRAM_EMV' },
  ENGAGEMENT_RATE: {
    value: 'ENGAGEMENT_RATE',
    label: 'Engagement Rate',
    sortValue: 'INSTAGRAM_ENGAGEMENT_RATE',
  },
  FOLLOWERS_GAINED: {
    value: 'FOLLOWERS_GAINED',
    label: 'Followers Gained',
    sortValue: 'INSTAGRAM_FOLLOWERS_GAINED',
  },
  IMPRESSIONS: {
    value: 'IMPRESSIONS',
    label: 'Impressions',
    sortValue: 'INSTAGRAM_TOTAL_IMPRESSIONS',
  },
  VIDEO_VIEWS: { value: 'VIDEO_VIEWS', label: 'Views', sortValue: 'INSTAGRAM_VIDEO_VIEWS' },
  LIKES: { value: 'LIKES', label: 'Likes', sortValue: 'INSTAGRAM_TOTAL_LIKES' },
  REACH: { value: 'REACH', label: 'Reach', sortValue: 'INSTAGRAM_REACH' },
  RECENTLY_PUBLISHED: {
    value: 'RECENTLY_PUBLISHED',
    label: 'Recently Published',
    sortValue: 'DATE',
  },
  SAVES: { value: 'SAVES', label: 'Saves', sortValue: 'INSTAGRAM_SAVES' },
  TOTAL_ENGAGEMENT: {
    value: 'TOTAL_ENGAGEMENT',
    label: 'Total Engagement',
    sortValue: 'INSTAGRAM_TOTAL_ENGAGEMENTS',
  },
};
export const instagramRelationshipStorySortOptions = {
  COMPLETION_RATE: {
    value: 'COMPLETION_RATE',
    label: 'Completion Rate',
    sortValue: 'INSTAGRAM_STORY_FRAME_COMPLETION_RATE',
  },
  COMPLETION_RATE_VIEWS: {
    value: 'COMPLETION_RATE_VIEWS',
    label: 'Completion Rate',
    sortValue: 'INSTAGRAM_STORY_FRAME_COMPLETION_RATE_VIEWS',
  },
  EXITS: { value: 'EXITS', label: 'Exits', sortValue: 'INSTAGRAM_STORY_FRAME_EXITS' },
  EXIT_RATE: {
    value: 'EXIT_RATE',
    label: 'Exit Rate',
    sortValue: 'INSTAGRAM_STORY_FRAME_EXIT_RATE',
  },
  EXIT_RATE_VIEWS: {
    value: 'EXIT_RATE_VIEWS',
    label: 'Exit Rate',
    sortValue: 'INSTAGRAM_STORY_FRAME_EXIT_RATE_VIEWS',
  },
  REACH: { value: 'REACH', label: 'Reach', sortValue: 'INSTAGRAM_STORY_FRAME_REACH' },
  RECENTLY_PUBLISHED: {
    value: 'RECENTLY_PUBLISHED',
    label: 'Recently Published',
    sortValue: 'DATE',
  },
  VIDEO_VIEWS: { value: 'VIDEO_VIEWS', label: 'Views', sortValue: 'INSTAGRAM_STORY_FRAME_VIEWS' },
};

export const instagramRelationshipPostTypeOptions = {
  POSTS: { value: 'POSTS', label: 'Posts' },
  STORIES: { value: 'STORIES', label: 'Stories' },
};

export const instagramCollaborationPostTypeOptions = {
  POSTS: { value: 'POST', label: 'Posts' },
  REELS: { value: 'REEL', label: 'Reels' },
};

export const creatorPostsDefaultOptions = {
  postSort: {
    sortOrder: 'DESC',
    selected: {
      value: 'RECENTLY_PUBLISHED',
      label: 'Recently Published',
    },
  },
  storySort: {
    sortOrder: 'DESC',
    selected: {
      value: 'RECENTLY_PUBLISHED',
      label: 'Recently Published',
    },
  },
  postType: instagramRelationshipPostTypeOptions.POSTS,
  youtubePostType: youtubePostTypeOptions.ALL,
  dateRangeFilter: {
    preset: 'SINCE_JAN_2022',
    presetObject: { value: 'SINCE_JAN_2022', label: 'Since Jan 1, 2022' },
    range: {
      start: new Date(2022, 0, 1),
      end: new Date(),
    },
  },
  presets: [
    {
      value: 'SINCE_JAN_2022',
      label: 'Since Jan 1, 2022',
      tooltipIcon:
        'Post metrics are only available for content published from January 1, 2022 onward',
      range: ({ dateService } = {}) => {
        const startDate = new Date(2022, 0, 1);
        const today = dateService ? dateService.now().toDate() : new Date();
        return getDateRangeFromDates(startDate, today, dateService);
      },
    },
    {
      value: 'LAST_24_HRS',
      label: 'Last 24 hrs',
      range: ({ dateService } = {}) => getDateRangeOffsetFromToday(1, dateService),
    },
    {
      value: 'LAST_3_DAYS',
      label: 'Last 3 Days',
      range: ({ dateService } = {}) => getDateRangeOffsetFromToday(3, dateService),
    },
    {
      value: 'LAST_7_DAYS',
      label: 'Last 7 Days',
      range: ({ dateService } = {}) => getDateRangeOffsetFromToday(7, dateService),
    },
    {
      value: 'LAST_4_WEEKS',
      label: 'Last 4 weeks',
      range: ({ dateService } = {}) => getDateRangeOffsetFromToday(30, dateService),
    },
  ],
};

export const tikTokCreatorDefaultSort = {
  sortOrder: 'DESC',
  selected: {
    value: 'RECENTLY_PUBLISHED',
    label: 'Recently Published',
  },
};

export const youtubeCreatorDefaultSort = {
  sortOrder: 'DESC',
  selected: {
    value: 'RECENTLY_PUBLISHED',
    label: 'Recently Published',
  },
};

export const creatorInvitationSelectData = {
  ACCEPTED: {
    label: inviteStatusDetails.ACCEPTED.label,
    value: inviteStatusDetails.ACCEPTED.value,
    tooltip: inviteStatusDetails.ACCEPTED.tooltip,
  },
  INVITATION_SENT: {
    label: inviteStatusDetails.INVITATION_SENT.label,
    value: inviteStatusDetails.INVITATION_SENT.value,
    tooltip: inviteStatusDetails.INVITATION_SENT.tooltip,
  },
  LAPSED: {
    label: inviteStatusDetails.LAPSED.label,
    value: inviteStatusDetails.LAPSED.value,
    tooltip: inviteStatusDetails.LAPSED.tooltip,
  },
  REVOKED: {
    label: inviteStatusDetails.REVOKED.label,
    value: inviteStatusDetails.REVOKED.value,
    tooltip: inviteStatusDetails.REVOKED.tooltip,
  },
  NOT_INVITED: {
    label: inviteStatusDetails.NOT_INVITED.label,
    value: inviteStatusDetails.NOT_INVITED.value,
    tooltip: inviteStatusDetails.NOT_INVITED.tooltip,
  },
  DISCONNECTED: {
    label: inviteStatusDetails.DISCONNECTED.label,
    value: inviteStatusDetails.DISCONNECTED.value,
    tooltip: inviteStatusDetails.DISCONNECTED.tooltip,
  },
  NOT_PROFESSIONAL: {
    label: 'Cannot Connect',
    value: 'NOT_PROFESSIONAL',
    tooltip: 'Only professional accounts can be connected',
  },
};

export const tikTokDiscoveryFilterPanels = {
  CREATORS: 'creators',
  CREATORS_AUDIENCE: 'creatorsAudience',
};

export const tiktokCreatorTypeOptions = [
  { title: 'Creators', value: tikTokDiscoveryFilterPanels.CREATORS },
  {
    title: 'Creator Followers',
    value: tikTokDiscoveryFilterPanels.CREATORS_AUDIENCE,
  },
];

const tiktokRIQTooltips = {
  views: 'The TikTok API does not provide a total for posts with under 1,000 video views',
};

export const creatorPostsTypeConfigs = {
  IG_BRANDED_CONTENT: {
    value: 'IG_BRANDED_CONTENT',
    metricKeys: instagramBrandedContentMetrics,
    metricLabels: instagramBrandedContentMetricLabels,
  },
  IG_FEED: {
    value: 'IG_FEED',
    metricKeys: instagramPostMetrics,
    metricLabels: instagramPostMetricLabels,
  },
  IG_RIQ: {
    value: 'IG_RIQ',
    metricKeys: instagramRelationshipPostMetrics,
    metricLabels: instagramRelationshipPostMetricLabels,
    metricUpdateLabels: instagramRelationshipPostMetricUpdateLabels,
  },
  IG_COLLABORATIONS: {
    value: 'IG_COLLABORATIONS',
    metricKeys: instagramRelationshipPostMetrics,
    metricLabels: instagramRelationshipPostMetricLabels,
  },
  YT_RIQ: {
    value: 'YT_RIQ',
    defaultListType: youtubePostListTypeOptions.RIQ,
    metricKeys: youtubeRelationshipPostMetrics,
    metricLabels: youtubeRelationshipPostMetricLabels,
  },
  IG_STORY: {
    value: 'IG_STORY',
    metricKeys: instagramStoryRelationshipPostMetrics,
    metricLabels: instagramStoryRelationshipPostMetricLabels,
    metricUpdateKeys: instagramStoryRelationshipPostMetricsUpdate,
    metricUpdateLabels: instagramStoryRelationshipPostMetricUpdateLabels,
  },
  TT_ACCOUNT_FEED: {
    value: 'TT_ACCOUNT_FEED',
    metricKeys: tikTokAccountFeedPostMetrics,
    metricLabels: tikTokAccountFeedPostMetricLabels,
  },
  TT_CREATOR_FEED: {
    value: 'TT_CREATOR_FEED',
    defaultListType: tikTokNotConnectedRIQPostListTypeOptions.FEED,
    metricKeys: tikTokCreatorFeedPostMetrics,
    metricLabels: tikTokCreatorFeedPostMetricLabels,
  },
  TT_CREATOR_FEED_CONNECTED: {
    value: 'TT_CREATOR_FEED_CONNECTED',
    defaultListType: tikTokConnectedRIQPostListTypeOptions.FEED,
    metricKeys: tikTokCreatorFeedMetricsConnected,
    metricLabels: tikTokCreatorFeedConnectedMetricLabels,
  },
  TT_CREATOR_RIQ_CONNECTED: {
    value: 'TT_CREATOR_RIQ_CONNECTED',
    defaultListType: tikTokConnectedRIQPostListTypeOptions.RIQ,
    metricKeys: tikTokCreatorRIQMetricsConnected,
    metricLabels: tikTokCreatorRIQConnectedMetricLabels,
    metricTooltips: tiktokRIQTooltips,
  },
  TT_CREATOR_RIQ_NOT_CONNECTED_OR_NOT_IN_TTCM: {
    value: 'TT_CREATOR_RIQ_NOT_CONNECTED_OR_NOT_IN_TTCM',
    defaultListType: tikTokNotConnectedRIQPostListTypeOptions.RIQ,
    metricKeys: tikTokCreatorRIQMetricsNotConnected,
    metricLabels: tikTokCreatorRIQNotConnectedMetricLabels,
    metricTooltips: tiktokRIQTooltips,
  },
  YT_FEED: {
    value: 'YT_FEED',
    defaultListType: youtubePostListTypeOptions.FEED,
    metricKeys: youtubePostMetrics,
    metricLabels: youtubePostMetricLabels,
  },
};

export const tikTokCreatorInTTCMTooltip =
  'This creator is part of the TikTok One Creator Marketplace';

export const instagramPostTypeForInsights = {
  FEED_POSTS: {
    label: 'Post',
    value: 'FEED_POSTS',
  },
  STORY: {
    label: 'Story',
    value: 'STORY',
  },
};

export const EMAIL_RESPONSE_TYPES = Object.freeze({
  reply: 'REPLY',
  replyAll: 'REPLY_ALL',
  forward: 'FORWARD',
});

export const SPARK_ADS_AUTHORIZATION_PERIOD_OPTIONS = Object.freeze([
  { value: 7, label: '7 Days' },
  { value: 30, label: '30 Days' },
  { value: 60, label: '60 Days' },
  { value: 365, label: '365 Days' },
]);

export const INSTAGRAM_HANDLE_REGEX =
  /^([A-Za-z0-9_](?:(?:[A-Za-z0-9_]|(?:\.(?!\.))){0,28}(?:[A-Za-z0-9_]))?)$/;

export const TIKTOK_HANDLE_REGEX = /^[a-zA-Z0-9_.]{1,23}[a-zA-Z0-9_]$/;

export const YOUTUBE_HANDLE_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9._-]{1,28}[a-zA-Z0-9]$/;

export const CREATOR_CAMPAIGNS_FILTER_OPTIONS = Object.freeze([
  { label: 'All Campaigns', value: 'ALL' },
  {
    label: 'Active Campaigns',
    value: 'ACTIVE',
    tooltipIcon: 'Campaigns that are currently live and running',
  },
  {
    label: 'Inactive Campaigns',
    value: 'INACTIVE',
    tooltipIcon: 'Campaigns that have ended or are no longer running',
  },
]);

export const metaPaidPartnersStatuses = Object.freeze({
  APPROVED: {
    label: 'Approved',
    severity: 'success',
    tooltip:
      'This account has accepted your partnership ads request. You can promote their content via Ads Manager.',
  },
  PENDING: {
    label: 'Pending',
    severity: 'statusNeutral',
    tooltip: "Partnership ads permission requested. Awaiting creator's response.",
  },
  CANCELED: {
    label: 'Revoked',
    severity: 'warning',
    tooltip:
      'You have revoked this partnership ads request. The creator can no longer authorize your permission to promote their content.',
  },
  REVOKED: {
    label: 'Declined',
    severity: 'warning',
    tooltip:
      'The creator has declined this partnership ads request. You will not be able to promote their content featuring you until they accept a new request.',
  },
});

export const CREATOR_CSV_FIELDS = {
  SOCIAL_HANDLES: [
    { name: 'Instagram Handle', channel: { name: 'Instagram', icon: 'instagram-color' } },
    { name: 'TikTok Handle', channel: { name: 'TikTok', icon: 'tiktok-color' } },
    { name: 'YouTube Handle', channel: { name: 'YouTube', icon: 'youtube-color' } },
  ],
  CREATOR_DETAILS: [
    {
      name: 'Creator Tags',
      tooltip: 'Tags that you assign to creators for easier organization and filtering',
    },
    { name: 'Full Name' },
    { name: 'Email Address' },
    { name: 'Phone Number' },
    { name: 'Birthday' },
    { name: 'Country' },
    { name: 'Mailing Address' },
    { name: 'City/Town/Department' },
    { name: 'State/Province/Region' },
    { name: 'ZIP/Postal Code' },
    { name: 'Notes' },
  ],
};

export const profileHeaderLocationTooltipAvailable =
  "This creator's estimated location, based on publicly available profile data";
export const profileHeaderLocationTooltipLoading = 'Processing the location of this creator';
export const profileHeaderLocationTooltipUnavailable =
  'Unable to estimate the location of this creator';

export const creatorPayorStatuses = Object.freeze({
  SUSPENDED: 'suspended',
  APPROVED: 'approved',
  PENDING: 'pending',
});

export const creatorPayorPendingBanner = {
  title: 'Account Pending',
  message:
    "We're reviewing your details, which usually takes 3 to 5 business days. You'll be able to add payees and send payments as soon as your account is approved.",
};

export const fundDepositBannerMessage =
  "After you submit, we'll send you an invoice within 2 business days of your chosen invoice date. Once we complete your brand compliance check, your balance will update within 3 business days of payment. To add funds anytime, go to Settings → Creator Payor Profile.";

export const payorBusinessCategories = [
  { label: 'Online Market', value: 'online_market' },
  { label: 'App Store', value: 'app_store' },
  { label: 'Affiliate Platform', value: 'affiliate_platform' },
  { label: 'Ad Network', value: 'ad_network' },
  { label: 'Crowd Funding', value: 'crowdfunding' },
  { label: 'Crowd Sourcing', value: 'crowdsourcing' },
  { label: 'Share Economy', value: 'share_economy' },
  { label: 'E-Commerce', value: 'e-commerce' },
  { label: 'Charity', value: 'charity' },
  { label: 'Surveys', value: 'surveys' },
  { label: 'Rebates', value: 'rebates' },
  { label: 'Startup', value: 'startup' },
  { label: 'Publishing', value: 'publishing' },
  { label: 'Entertainment', value: 'entertainment' },
  { label: 'Travel', value: 'travel' },
  { label: 'Education', value: 'education' },
  { label: 'Manufacturing', value: 'manufacturing' },
  { label: 'Business Service', value: 'business_service' },
  { label: 'Influencer Platform', value: 'influencer_platform' },
  { label: 'Online Gambling', value: 'online_gambling' },
  { label: 'Adult Entertainment', value: 'adult_entertainment' },
  { label: 'Multi Level Marketing', value: 'multi_level_marketing' },
  { label: 'Firearms', value: 'firearms' },
  { label: 'Money Transmitter Service', value: 'money_transmitter_service' },
  { label: 'Credit Card Processing', value: 'credit_card_processing' },
  { label: 'Other', value: 'other' },
];

export const payeeTypes = [
  { label: 'Business', value: 'BUSINESS' },
  { label: 'Individual', value: 'INDIVIDUAL' },
];

export const payeeTypeDisplayNames = {
  business: 'Business',
  individual: 'Individual',
};

export const editPayeeStatusOptions = {
  [PayeeStatus.ACTIVE]: [
    {
      value: PayeeStatus.ACTIVE,
      label: 'Active',
      hide: true,
    },
    {
      value: PayeeStatus.DISABLED,
      label: 'Deactivate',
      controlIcon: 'slash-circle',
      severity: 'danger',
      tooltipIcon:
        'A deactivated payee can’t receive payments until you reactivate them. You can reactivate them anytime.',
    },
  ],
  [PayeeStatus.DISABLED]: [
    {
      value: PayeeStatus.ACTIVE,
      label: 'Active',
      controlIcon: 'check',
      tooltipIcon: 'The creator is fully set up and can receive payments',
    },
    {
      value: PayeeStatus.DISABLED,
      label: 'Deactivate',
      hide: true,
    },
  ],
  [PayeeStatus.INCOMPLETE]: [
    {
      value: PayeeStatus.INCOMPLETE,
      label: 'Incomplete',
      hide: true,
    },
  ],
  [PayeeStatus.BLOCKED]: [
    {
      value: PayeeStatus.BLOCKED,
      label: 'Blocked',
      hide: true,
    },
  ],
  [PayeeStatus.ARCHIVED]: [
    {
      value: PayeeStatus.ARCHIVED,
      label: 'Archived',
      hide: true,
    },
  ],
  [PayeeStatus.SUSPENDED]: [
    {
      value: PayeeStatus.SUSPENDED,
      label: 'Suspended',
      hide: true,
    },
  ],
};

export const payeesStatusChipSeverity = {
  [PayeeStatus.ACTIVE]: 'success',
  [PayeeStatus.INCOMPLETE]: 'warning',
  [PayeeStatus.DISABLED]: 'statusNeutral',
  [PayeeStatus.BLOCKED]: 'danger',
  [PayeeStatus.ARCHIVED]: 'statusNeutral',
  [PayeeStatus.SUSPENDED]: 'danger',
};

export const payeesStatusChipDisplay = {
  [PayeeStatus.ACTIVE]: {
    badge: {
      severity: payeesStatusChipSeverity.active,
    },
    label: 'Active',
    tooltip: payeeStatusTooltips.active,
  },
  [PayeeStatus.INCOMPLETE]: {
    badge: {
      severity: payeesStatusChipSeverity.incomplete,
    },
    label: 'Incomplete',
    tooltip: payeeStatusTooltips.incomplete,
  },
  [PayeeStatus.DISABLED]: {
    badge: {
      severity: payeesStatusChipSeverity.disabled,
    },
    label: 'Deactivated',
    tooltip: payeeStatusTooltips.deactivated,
  },
  [PayeeStatus.BLOCKED]: {
    badge: {
      severity: payeesStatusChipSeverity.blocked,
    },
    label: 'Blocked',
    tooltip: payeeStatusTooltips.blocked,
  },
  [PayeeStatus.ARCHIVED]: {
    badge: {
      severity: payeesStatusChipSeverity.archived,
    },
    label: 'Archived',
    tooltip: payeeStatusTooltips.archived,
  },
  [PayeeStatus.SUSPENDED]: {
    badge: {
      severity: payeesStatusChipSeverity.suspended,
    },
    label: 'Suspended',
    tooltip: payeeStatusTooltips.suspended,
  },
};

// Maps Trolley payment statuses (https://developers.trolley.com/api/#payment-status)
// to how we display them. Keep this 1-to-1 with Trolley's statuses so we can
// rename what we call each state without changing the source values.
export const paymentStatusChipDisplay = {
  pending: { label: 'Pending', severity: 'neutral' },
  processing: { label: 'Processing', severity: 'neutral' },
  processed: { label: 'Processed', severity: 'success' },
  failed: { label: 'Failed', severity: 'danger' },
  returned: { label: 'Returned', severity: 'danger' },
};

export const paymentStatusTooltip = {
  pending: 'This payment is queued and will start processing automatically',
  processing:
    'This payment is on its way. The status updates on its own once processing finishes. Payments created on a weekend or after the Friday cut-off stay here until processing resumes.',
  processed: 'The payment was sent successfully',
  failed:
    "This payment couldn't be processed and can't be retried. Create a new payment to try again.",
  returned:
    'This payment was sent but came back as undeliverable. Prompt the Creator to re-enter their payout details, then create a new payment.',
};

export const payoutMethodDisplayName = {
  paypal: 'PayPal',
  'bank-transfer': 'Bank Transfer',
  check: 'Check',
};

export const maxTrolleyEmailLength = 79;

export const shopifyFulfillmentStatusLabels = {
  FULFILLED: 'Fulfilled',
  IN_PROGRESS: 'In Progress',
  ON_HOLD: 'On Hold',
  PARTIALLY_FULFILLED: 'Partially Fulfilled',
  REQUEST_DECLINED: 'Request Declined',
  SCHEDULED: 'Scheduled',
  UNFULFILLED: 'Unfulfilled',
  ERROR: 'Error',
};

export const shopifyFulfillmentStatusData = {
  FULFILLED: {
    label: 'Order Confirmed',
    tooltip: null,
    step: 4,
    badgeSeverity: 'success',
    barSeverity: 'positive',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    tooltip: null,
    step: 2,
    badgeSeverity: 'info',
    barSeverity: 'neutral',
  },
  ON_HOLD: {
    label: 'On Hold',
    tooltip:
      'All unfulfilled products in this order are currently on hold. Check your Shopify admin to learn more or release the hold.',
    step: 2,
    badgeSeverity: 'neutral',
    barSeverity: 'neutral',
  },
  PARTIALLY_FULFILLED: {
    label: 'Partially Fulfilled',
    tooltip:
      'Some products in this order have been delivered. Others are still being processed. Check your Shopify admin for more details.',
    step: 2,
    badgeSeverity: 'info',
    barSeverity: 'neutral',
  },
  REQUEST_DECLINED: {
    label: 'Request Declined',
    tooltip:
      'The product request was declined by the fulfillment team and cannot be completed. Check your Shopify admin to learn more.',
    step: 0,
    badgeSeverity: 'danger',
    barSeverity: 'danger',
  },
  SCHEDULED: {
    label: 'Scheduled',
    tooltip: 'All unfulfilled products are scheduled to ship at a later time.',
    step: 2,
    badgeSeverity: 'neutral',
    barSeverity: 'neutral',
  },
  UNFULFILLED: {
    label: 'Unfulfilled',
    tooltip: "Order confirmed. Products haven't been fulfilled or shipped yet.",
    step: 1,
    badgeSeverity: 'neutral',
    barSeverity: 'neutral',
  },
};

export const payeesTermsOfService = `
<p>By selecting “I Agree” or otherwise accessing the payment feature facilitated by the Dash Social Platform (“Payment Feature”), you (“Creator, “you”, “your””) agree to the following Creator Payment Terms (“Terms”). These terms apply solely to your receipt of payments from the Dash Social User (“Payment Requestor”) who engaged you. These Terms do not grant you access to other Dash Social services or create any broader relationship between you and Dash Hudson Inc. (“Dash Social”).</p>
<p><b>1. Relationship.</b> These Terms govern your use of the Payment Feature solely for receiving payments from the Payment Requestor. You are not entering into any agreement with Dash Social, and no employment, contractor, agency, partnership, or other relationship is created between you and Dash Social. Dash Social is not a party to, and has no responsibility for, any agreement, arrangement, campaign, engagement, deliverable, service, or other relationship between you and the Payment Requestor. All payment obligations are solely between you and the Payment Requestor. Dash Social does not review, monitor, approve, guarantee, verify, or assume responsibility for any campaign, deliverable, content, service, or obligation between you and the Payment Requestor and shall have no liability arising from or relating thereto.</p>
<p><b>2. Payment Processing.</b> Payments to you will be facilitated by Dash Social’s authorized payment partner, a registered Money Services Business, and its affiliated financial institutions (collectively, the “Payment Provider”). By using the Payment Feature, you authorize Dash Social and the Payment Provider to receive, hold, process, and disburse funds to you as required to complete payment orders initiated by the Payment Requestor. All payment instructions are initiated and authorized by the Payment Requestor. Dash Social does not determine whether, when, or in what amount a payment will be made.</p>
<p>Your use of the Payment Feature is also subject to the terms, conditions, and requirements of the Payment Provider and applicable financial institutions. Dash Social is not responsible for the acts or omissions of the Payment Provider or any financial institution.</p>
<p>You authorize Dash Social and the Payment Provider to recover, offset, debit, withhold, or reverse payments made to you where such payments were made in error, duplicated, fraudulent, subject to chargeback, reversal, legal requirement, or otherwise improperly received. If recovery is unsuccessful, you agree to promptly reimburse such amounts upon request.</p>
<p>Dash Social has no responsibility or liability for the initiation, funding, timing, accuracy, or completion of any payment transaction. Payments may be delayed, fail, or be reversed due to insufficient funds, fraud, compliance requirements, payment network rules, or other factors outside of Dash Social’s control.</p>
<p><b>3. Account Information.</b> You agree to provide accurate and complete payment, banking, tax, and identification information and to keep it current. You authorize Dash Social and the Payment Provider to verify such information and to share it as necessary to process payments and comply with applicable law. You acknowledge and agree that your information will be shared with the Payment Provider and its partners for purposes of identity verification, compliance, and payment processing.</p>
<p>You understand that incomplete, inaccurate, or improperly formatted information may delay, prevent, or result in rejection of a payment order. To the extent any funds are misdirected, delayed, or not received as a result of inaccurate, incomplete, or improperly formatted information provided by you, such funds may not be recoverable, and Dash Social shall have no liability in connection with such funds. Dash Social and the Payment Provider may, but are not obligated to, attempt to recover such funds, and any applicable recovery fees or third-party charges incurred in connection with such efforts may be charged to you.</p>
<p>Payments may be subject to currency conversion, banking fees, intermediary charges, or other deductions imposed by financial institutions or payment networks, which may reduce the amount received by you. Dash Social has no control over and is not responsible for such fees or deductions.</p>
<p>You agree to promptly provide any information reasonably requested by Dash Social or the Payment Provider for identity verification, fraud prevention, compliance reviews, anti-money laundering obligations, tax reporting, investigations, regulatory inquiries, legal process, or other lawful purposes related to the operation of the Payment Feature.</p>
<p><b>4. Execution and Cut-Off Times.</b> Payment instructions will be processed in accordance with applicable legal requirements, and the Payment Provider’s processing timelines.</p>
<p><b>5. Taxes.</b> You are solely responsible for determining, reporting, and paying any taxes arising from payments you receive through the Payment Feature in accordance with applicable law. You agree to provide accurate, complete, and current tax identification and registration information as requested by Dash Social or the Payment Provider, and to promptly update such information if it changes. Dash Social or the Payment Provider may collect tax documentation, verify tax information, and issue tax reporting forms as required by applicable law. Failure to provide required tax information may result in withholding of payments, suspension or blocking of payments, or other actions required under applicable law. Payments may also be subject to mandatory tax withholding where required by law.</p>
<p><b>6. Compliance and Restrictions.</b> You must not use the Payment Feature for any illegal, fraudulent, or prohibited purpose, including but not limited to activities that violate sanctions, anti-money-laundering, or counter-terrorist-financing laws.</p>
<p>Dash Social or the Payment Provider may suspend, delay, reject, reverse, cancel, or withhold any payment transaction where reasonably necessary to comply with applicable law, payment network requirements, fraud prevention measures, court orders, governmental requests, regulatory obligations, identity verification requirements, or to protect the security and integrity of the Payment Feature.</p>
<p><b>7. Service Modifications.</b> Dash Social and Payment Provider may update or modify the Payment Feature from time to time. You agree to accept all reasonable updates or patches required for the secure and proper functioning of the Payment Feature. You acknowledge that the Payment Feature may be unavailable from time to time due to repairs, upgrades, routine and emergency maintenance, malfunction of computer or network equipment, or other interruptions that may be out of Dash Social and Payment Provider’s reasonable control. Certain security-related updates may be implemented with limited notice.</p>
<p><b>8. Disputes.</b> Any disputes regarding: (a) whether payment is owed; (b) payment amounts; (c) campaign or deliverable performance; or (d) the terms of your engagement are solely between you and the Payment Requestor. Dash Social does not mediate such disputes and is not responsible for delays, reversals, or errors caused by the Payment Requestor, financial institutions, or the Payment Provider.</p>
<p>Dash Social has no obligation to monitor, review, or verify any payment request, transaction, or underlying engagement between you and the Payment Requestor.</p>
<p><b>9. Payment Services.</b> Dash Social is not a bank, money transmitter, money services business, or other regulated financial institution. The Payment Feature is provided solely as a technology tool that facilitates payment transactions between Payment Requestors and Creators through the Payment Provider.</p>
<p>Dash Social may facilitate the transfer of funds in connection with payment transactions, but all payment processing, compliance screening, identity verification, and payment disbursement services are performed by the Payment Provider and applicable financial institutions.</p>
<p>Dash Social does not guarantee the timing, availability, receipt, or successful completion of any payment transaction and shall not be liable for any delay, failure, reversal, withholding, or non-payment of funds arising from the acts or omissions of the Payment Requestor, Payment Provider, financial institutions, or applicable legal or regulatory requirements.</p>
<p><b>10. Suspension or Termination.</b> Dash Social may suspend, restrict, or terminate your access to the Payment Feature at any time, in its sole discretion, with or without notice. This may occur for any reason, including non-compliance with these Terms, suspected fraud, failure to provide required information, security concerns, requirements of the Payment Provider, regulatory obligations, or applicable law. Dash Social shall have no liability arising from any such suspension or termination.</p>
<p><b>11. Disclaimer.</b> The Payment Feature is provided on an “as-is” and “as-available” basis. Dash Social does not guarantee that any payment will be made to you and is not responsible for delays, errors, or failures caused by the Payment Requestor, applicable financial institutions, or the Payment Provider. Dash Social does not provide financial, legal, tax, or professional advice.</p>
<p>To the maximum extent permitted by applicable law, Dash Social disclaims all warranties, representations, and conditions, whether express, implied, statutory, or otherwise, including any implied warranties of merchantability, fitness for a particular purpose, title, non-infringement, or uninterrupted or error-free operation of the Payment Feature.</p>
<p><b>12. Limitation of Liability.</b> To the maximum extent permitted by applicable law, Dash Social shall not be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages, or for any loss of profits, revenue, goodwill, business opportunity, anticipated savings, or data, arising out of or relating to the Payment Feature or these Terms, even if advised of the possibility of such damages.</p>
<p>To the maximum extent permitted by applicable law, Dash Social's aggregate liability arising out of or relating to the Payment Feature or these Terms shall not exceed the greater of: (a) CAD $100; or (b) the amount of fees, if any, paid by you directly to Dash Social for use of the Payment Feature during the twelve (12) months preceding the event giving rise to the claim.</p>
<p><b>13. Indemnity.</b> To the maximum extent permitted by law, you agree to indemnify, defend, and hold harmless Dash Social, its affiliates, and their respective officers, directors, employees, agents, contractors, successors, and assigns from and against any and all claims, demands, actions, investigations, proceedings, losses, liabilities, damages, judgments, settlements, penalties, fines, costs, and expenses (including reasonable legal fees) arising out of or relating to: (a) your use of the Payment Feature; (b) inaccurate, incomplete, misleading, or outdated information provided by you; (c) taxes, duties, levies, or other governmental charges relating to payments received by you; (d) any dispute, claim, investigation, or proceeding relating to your relationship, engagement, services, content, deliverables, or transactions with a Payment Requestor; (e) your breach of these Terms; (f) your violation of any applicable law, regulation, or payment network rule; (g) your violation of the rights of any third party, including intellectual property, privacy, publicity, or contractual rights; or (h) any fraud, misconduct, negligence, or wrongful act or omission by you. Dash Social may assume exclusive control of the defense and settlement of any matter subject to indemnification under this Section, and you agree to cooperate fully with such defense.</p>
<p><b>14. Survival.</b> Sections 3 (Account Information), 5 (Taxes), 8 (Disputes), 11 (Disclaimer), 12 (Limitation of Liability), 13 (Indemnity), and any other provision which by its nature should survive, shall survive the suspension, termination, or expiration of these Terms.</p>
<p><b>15. Acceptance.</b> By clicking “I Agree,” you confirm that you have read, understood, and agree to be bound by these Terms and acknowledge Dash Social’s Privacy Policy found here: <a class="text-action!" href="https://www.dashsocial.com/privacy" target="_blank" rel="noopener noreferrer">https://www.dashsocial.com/privacy</a>.</p>
`;
export const noCreatorPaymentsAccessConstants = {
  variant: 'upgrade',
  title: 'Ask Your Admin for Access',
  message:
    'To view Creator Payments, ask an admin on your account to grant you access. Open Teams and Permissions to view who your admins are.',
  icon: 'key',
  ctaButton: {
    label: 'Open Teams and Permissions',
    severity: 'tertiary',
  },
};

export const sendPaymentTooltips = {
  NO_ACCESS:
    'Contact an admin on your account for access to this feature or reach out to us for support',
  PAYOR_NOT_APPROVED: 'Creating Payments is blocked until your Payor profile is approved',
  PAYEE_NOT_ACTIVE_AND_TERMS_NOT_ACCEPTED:
    "Send a payment once this Payee is Active and has accepted Dash Social's Terms of Service",
  PAYEE_NOT_ACTIVE: "Send a payment once this Payee's status is Active",
  PAYEE_TERMS_NOT_ACCEPTED: "Send a payment once this Payee accepts Dash Social's Terms of Service",
  AMOUNT_TO_SEND: 'The full amount withdrawn from your available balance',
  CREATOR_RECEIVES:
    'The final amount the Creator receives after transaction & any applicable FX fees',
  CURRENCY:
    'The currency the Creator chose to be paid in. They can change it anytime in their payment profile.',
  FX_RATE:
    "The exchange rate applied when converting to the Creator's currency. Includes a 2% FX fee.",
  PAYMENT_AMOUNT:
    "The amount debited from your balance, including transaction and any applicable FX fees. The Creator's final payout shows on the next step.",
  TAX_WITHHOLDING:
    'Withholding is calculated automatically by Trolley based on the creator’s tax profile',
  TRANSACTION_FEE: 'Transaction and FX fees, covered by the Creator and deducted from their payout',
};

export const sendPaymentPayeeStatusTooltips = {
  [PayeeStatus.ACTIVE]: '',
  [PayeeStatus.INCOMPLETE]:
    'This creator hasn’t agreed to Dash Social’s Terms of Service or completed the onboarding process',
  [PayeeStatus.DISABLED]:
    'The creator can’t receive payments while their account is temporarily deactivated. You can reactivate them at any time',
  [PayeeStatus.BLOCKED]:
    'This creator was blocked by Trolley for compliance reasons. They can’t receive payments, but their details remain on file',
  [PayeeStatus.ARCHIVED]:
    'The creator was removed from your active list. Archived profiles can be unarchived if needed',
  [PayeeStatus.SUSPENDED]:
    'You’ve suspended this creator. They can’t receive payments or be linked to another account, but their profile details are still visible',
};

export const noAcceptedTermsTooltip =
  'This creator hasn’t agreed to Dash Social’s Terms of Service';

export const payPalAddFundsLink = 'https://www.paypal.com/fundstransfer/';

export const MIN_PAYMENT_AMOUNT = 5;

export const MAX_PAYMENT_MEMO_LENGTH = 1023;

export const postFilterOptions = [
  { value: 'ALL', label: 'All Posts' },
  { value: 'INCLUDED', label: 'Included Posts', tooltipIcon: 'Include posts from Campaign' },
  { value: 'EXCLUDED', label: 'Excluded Posts', tooltipIcon: 'Exclude posts from Campaign' },
];

export const invitationStatusChipDisplay = {
  NOT_INVITED: {
    label: 'Not Invited',
    value: 'NOT_INVITED',
    severity: 'neutral',
  },
  INVITATION_SENT: {
    label: 'Invite Sent',
    value: 'INVITATION_SENT',
    severity: 'neutral',
  },
  ACCEPTED: {
    label: 'Connected',
    value: 'ACCEPTED',
    severity: 'success',
  },
  LAPSED: {
    label: 'Expired',
    value: 'LAPSED',
    severity: 'neutral',
  },
  REVOKED: {
    label: 'Revoked',
    value: 'REVOKED',
    severity: 'neutral',
  },
  DISCONNECTED: {
    label: 'Disconnected',
    value: 'DISCONNECTED',
    severity: 'warning',
  },
  NOT_PROFESSIONAL: {
    label: 'Cannot Connect',
    value: 'NOT_PROFESSIONAL',
    severity: 'danger',
  },
};

export const COMPETITIVE_MENTIONS_BANNER_MESSAGE = `You can configure up to ${MAX_COMPETITORS} competitors in your filter`;
export const COMPETITOR_TAGS_BANNER_MESSAGE = `You can filter up to ${MAX_COMPETITORS} competitors in total across all tags`;

export const COMPETITIVE_MENTIONS_EMPTY_MESSAGES = {
  COMPETITORS: {
    DEFAULT: 'See which creators have mentioned your competitors in their content',
    SEARCH:
      "This competitor account isn't in your list. Add it in Settings to start tracking their mentions.",
  },
  COMPETITOR_TAGS: {
    DEFAULT: 'Organize your competitors into custom groups for faster filtering',
    SEARCH:
      "This competitor tag isn't in your list. Add it in Settings to group and track your competitors.",
  },
};

export const BITLY_CONNECTION_DETAILS = {
  CONNECTION_MESSAGE:
    'Connect your Bitly account to create branded affiliate links and monitor clicks in real time.',
  CONNECTION_CTA: 'Connect',
  LEARN_MORE_URL: 'https://help.dashsocial.com/hc/en-us/articles/40656147780493',
};

export const DM_STORY_BANNER_MESSAGE =
  'To view Stories metrics, creator must be connected at the time of posting';

export const STORY_METRIC_BASIS_BANNER_MESSAGE =
  'Story metrics shown in Your Creators are based on brand mentions only';

export const VISUAL_FILTER_INSTRUCTIONS =
  'Select a piece of media to discover creators that are posting visually similar content';

export const CREATOR_FIT_ERROR_MESSAGE =
  "We weren't able to show Creator Fit just now, refresh to get updated results";

export const CREATOR_FIT_NOT_AVAILABLE_MESSAGE = 'Creator Fit Score is not available';

// Fallback narration shown before the first status_update event lands.
export const CREATOR_FIT_GENERATING_MESSAGE =
  'Generating your content may take some time, so please be patient as Vision works.';

export const BRAND_FIT_CRITERION_LABELS = {
  predictedContentPerformance: 'Predicted Performance',
  ugcPresence: 'UGC Presence',
  creatorInterests: 'Creator Interests',
  creatorEngagement: 'Engagement Rate',
  audienceSize: 'Audience Size',
};

export const BRAND_FIT_CRITERION_TOOLTIPS = {
  predictedContentPerformance:
    'How the creator’s recent posts are predicted to perform for your brand, based on Vision AI’s analysis of your owned content',
  ugcPresence: 'How much of the creator’s recent content already mentions your brand',
  creatorInterests: 'How closely the creator’s content topics overlap with your brand’s interests',
  creatorEngagement:
    'How the creator’s average engagement rate compares to the benchmark for their follower tier in Your Creators or global industry benchmarks',
  audienceSize:
    'How the creator’s follower count compares to the range of creators in Your Creators',
};

export const BRAND_FIT_TIER_TOOLTIPS = {
  excellent:
    "This creator's audience, content, and performance closely align with your brand and rank among the strongest compared to creators in Your Creators",
  good: 'This creator aligns with your brand across most criteria and compares favourably to creators in Your Creators',
  weak: 'This creator shows limited alignment with your brand based on current criteria and benchmarks from Your Creators. Review the details below before moving forward',
  unknown:
    'Not enough data is available to determine fit. Check back once more content or creator data is collected',
};

// Payment History CSV export presets — to-date ranges are inclusive of today.
export const PAYMENT_EXPORT_DEFAULT_PRESET = 'MONTH_TO_DATE';

// dashing-ui hands its own `dateService` (the profile timezone) to every preset it resolves,
// so "now" has to come from there — a browser-local `dayjs()` picks the wrong day for anyone
// whose profile timezone differs from their machine's.
const presetNow = (dateService) => dateService?.now() ?? dayjs();

export const PAYMENT_EXPORT_PRESETS = [
  {
    value: 'MONTH_TO_DATE',
    label: 'Month to Date',
    range: ({ dateService } = {}) => {
      const now = presetNow(dateService);
      return { start: now.startOf('month').toDate(), end: now.endOf('day').toDate() };
    },
  },
  {
    value: 'LAST_MONTH',
    label: 'Last Month',
    range: ({ dateService } = {}) => {
      const lastMonth = presetNow(dateService).subtract(1, 'month');
      return { start: lastMonth.startOf('month').toDate(), end: lastMonth.endOf('month').toDate() };
    },
  },
  {
    value: 'YEAR_TO_DATE',
    label: 'Year to Date',
    range: ({ dateService } = {}) => {
      const now = presetNow(dateService);
      return { start: now.startOf('year').toDate(), end: now.endOf('day').toDate() };
    },
  },
  {
    value: 'LAST_YEAR',
    label: 'Last Year',
    range: ({ dateService } = {}) => {
      const lastYear = presetNow(dateService).subtract(1, 'year');
      return { start: lastYear.startOf('year').toDate(), end: lastYear.endOf('year').toDate() };
    },
  },
];
